import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';

import { translate } from '@jsverse/transloco';

import { saveAs } from 'file-saver-es';
import * as Highcharts from 'highcharts';

import { toCanvas } from 'html-to-image';
import moment from 'moment';
import { Moment } from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { v4 as UUIDv4 } from 'uuid';
import {
  RGBAColor,
  RGBAColorInput,
} from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { Constants } from 'app/constants/constants';

import {
  AnalyticItemFilterSets,
  DATE_TIME_WITHOUT_TIMEZONE_FORMAT,
  Filter,
  FilterService,
  UserSearchFilter,
  WidgetQueryOptions,
} from 'app/modules/collection/services/filter.service';
import { ChartCustomColorService } from 'app/modules/dashboard/services/chart-custom-color.service';
import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { PptxReport, SnapshotSlideObject } from 'app/modules/widget/services/pptx.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  Comment,
  CommentPoint,
  CommentTarget,
  Comparison,
  Dashboard,
  Widget,
  WidgetUtility,
} from 'app/providers/bridge-api/dashboard.service';
import { DocumentService, SearchOptions } from 'app/providers/storage-api/document.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { encodeParams } from 'app/providers/storage-api/report.service';
import { DownloadTasksService, TaskType } from 'app/providers/tasks-api/download.service';
import { AlertService } from 'app/services/alert.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetLimiterService } from 'app/services/widget-limiter.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { ScatterPlotUtils } from 'app/utils/scatter-plot-utils';
import { StringBuilder } from 'app/utils/string-builder';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { CpppCalculationService } from './../../services/cppp-calculation.service';
import { LexDatePipe } from '../../../../pipes/lex-date.pipe';
import { MatchmakerHandler } from '../../../../providers/semantria-api/spote.service';
import { StorageAccountService } from '../../../../providers/storage-api/blender-account.service';
import {
  DEFAULT_PROJECT_COLOR_PALETTE,
  ProjectService,
} from '../../../../providers/storage-api/project.service';
import { DialogService } from '../../../../services/dialog.service';
import { LoggerService } from '../../../../services/logger.service';
import { ChartOptionUtils } from '../../../../utils/chart-options.utils';
import { FilterSet } from '../../../collection/services/filter.service';
import { DashboardViewComponent } from '../../../dashboard/pages/dashboard-view/dashboard-view.component';
import { ChartGoalShadeDirection } from '../chart-options-dialog/chart-options-dialog.component';
import { Story } from '../newspaper-widglet/journalists/journalist';
import { NumericMetadataField } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.models';
import { SelectedSection } from '../section-selector/section-selector.component';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import {
  ChartAiInsightPointSelectionService,
  ChartPointSelectionMatchmakerOptions,
} from '../widget-expansion-panel/widget-ai-insights/chart-ai-insight-point-selection.service';
import { ChartWidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/chart-widget-ai-insights-validator.service';
import { WidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/widget-ai-insights-validator.service';
import { JourneyDrillLevel, JourneyDrillRange } from '../widget-journey/widget-journey.component';
import { ChartCommentsService } from '../../services/chart-comments.service';
import { ChartDataService } from '../../services/chart-data.service';
import {
  ChartDisplayService,
  HighchartsOptions,
  HighchartsRenderEvent,
} from '../../services/chart-display.service';
import { AbstractChartReport } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';
import { ChartReportService } from '../../services/chart-report.service';
import { ChartSortHelper } from '../../services/chart-sort-helper';
import { ChartTableService } from '../../services/chart-table.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export const ChartDateInterval = [
  'minute',
  'hour',
  'day',
  'week',
  'month',
  'quarter',
  'year',
] as const;
export type ChartDateInterval = (typeof ChartDateInterval)[number];

export type ChartAxisValues = 'default' | 'custom';

export interface WidgetChartOptions {
  projectId?: string;
  sentimentScale?: number;
  analyticItem:
    | 'documents'
    | 'topics'
    | 'entities'
    | 'phrases'
    | 'themes'
    | 'metadata'
    | 'taxonomy'
    | string;
  entityTypeFilter: string;
  limit?: number;
  selectedItems?: string[];
  zeroPadding?: boolean;
  customItemColors?: any[];
  type:
    | 'documentCount'
    | 'numericMetadata'
    | 'sentimentScore'
    | 'sentimentPolarity'
    | 'sentimentVolume'
    | 'taxonomyMap'
    | 'topMovers'
    | 'wordcloud'
    | 'crosstab'
    | 'featureScoring'
    | 'sentimentAccuracy'
    | 'scatterPlot'
    | 'impact'
    | 'bigNumber'
    | 'breakdown'
    | 'donut'
    | 'gauge'
    | 'powerScore'
    | 'powerScoreOverTime'
    | 'focusAreas'
    | 'journey';
  aggregator?:
    | 'total'
    | 'time'
    | 'percentage'
    | 'AVG'
    | 'SUM'
    | 'total_AVG'
    | 'total_SUM'
    | 'percentage_time';
  graphType?:
    | 'column'
    | 'bar'
    | 'pie'
    | 'wordcloud'
    | 'line'
    | 'spline'
    | 'stackedarea'
    | 'stackedbar'
    | 'crosstab'
    | 'gauge'
    | 'dependencywheel'
    | 'timeline';
  dateInterval?: ChartDateInterval;
  excludedValues?: {
    name: string;
    type: string;
    subType?: string;
    displayName?: string;
    axis?: 'row' | 'column' | 'all';
  }[];
  filters?: AnalyticItemFilterSets;
  filteredDocumentCount?: number;
  currentPeriodDocumentCount?: number;
  previousPeriodDocumentCount?: number;
  comparisonBucketsCounts?: number[];
  orderBySentiment?: boolean;
  widgetDataSource: WidgetDataSource;
  widgetDataSourceId: string;
  zoomDateFilter?: string;
  refresh?: (...args) => void;
  customFieldSelected?: boolean;
  standardFieldSelected?: boolean;
  sectionPropertySelected?: boolean;
  userSelectedDateRange?: boolean;
  topicPhraseCloud?: boolean;
  showBlanks?: boolean;
  drilledDownOn?:
    | 'themes'
    | 'mentions'
    | 'taxonomy'
    | 'taxonomy_mentions'
    | 'topic_phrases'
    | 'entity_phrases'
    | 'taxonomy_phrases'
    | 'taxonomy_themes'
    | 'none';
  isDrilledDown?: boolean;
  drilledDownLocation?: string;
  sort?: ChartSort;
  chartMaximum?: number;
  chartMaximumHorizontal?: number;
  chartMaximumPercentage?: number;
  chartMinimum?: number;
  chartInterval?: number;
  chartAxisValue?: ChartAxisValues;
  chartSecondaryAxisValue?: ChartAxisValues;
  chartSecondaryMaximum?: number;
  chartSecondaryMinimum?: number;
  chartSecondaryInterval?: number;
  legends?: { [name: string]: boolean };
  xAxis?: string;
  yAxis?: string;
  boxSize?: string;
  scoringStrategy?: string;
  boxDirection?: string;
  scoreRange?: string;
  numericMetadataFields?: NumericMetadataField[];
  subject?: string;
  subjectValue?: string;
  comparison?: string;
  breakdownOptions?: BreakdownOptions;
  chartGoal?: number;
  chartGoalDisplayAs: string;
  chartGoalColor: string;
  chartGoalShade?: ChartGoalShadeDirection;
  displayChartValues?: boolean;
  colorByPoint?: boolean;
  donutOptions?: DonutOptions;
  gaugeOptions?: GaugeOptions;
  weightField?: string;
  dateMarkers?: DateMarker[];
  selectedSection?: SelectedSection[];
  comparisonSectionFilters?: SelectedSection[];
  showAsStacked?: boolean;
  comments?: Comment[];
  commentsHidden?: boolean;
  userSearch?: UserSearchFilter;
  display?: {
    showAsPercentage?: boolean;
    decimalPrecision?: string | number;
  };
  showWeekPrefix?: boolean;
  newspaper?: {
    show: boolean;
    scene: string;
    generatedAt?: string;
    timezone?: string;
    dataSourceId: string;
    dataSourceModifiedAt?: string;
    weightField?: string;
    filterSetHash: number;
    comparisonHash: number;
    widgetGroupHash: number;
    widgetSettingsHash: number;
    stories: Story[];
  };
  additionalBreakdownOptions?: Partial<BreakdownOptions>;
  hasSecondaryAxis?: boolean;
  bucketName?: string;
}

export interface ChartSort {
  field: 'value' | 'label' | 'custom' | string;
  order: 'asc' | 'desc';
  sentiment?: 'positive' | 'negative' | 'Top' | 'Bottom' | 'total';
  items?: any[];
}

export const DEFAULT_SORT: ChartSort = {
  field: 'value',
  order: 'desc',
};

export const DEFAULT_SORT_SENTIMENT_POLARITY: ChartSort = {
  field: 'value',
  order: 'desc',
  sentiment: 'positive',
};

export const DEFAULT_SORT_BREAKDOWN_BOX: ChartSort = {
  field: 'value',
  order: 'desc',
  sentiment: 'Top',
};

interface ScoringDisplayOptions {
  isTopShown?: boolean;
  isNeutralShown?: boolean;
  isBottomShown?: boolean;
  topBoxSize?: BoxSize;
  bottomBoxSize?: BoxSize;
  topColor?: RGBAColorInput;
  neutralColor?: RGBAColorInput;
  bottomColor?: RGBAColorInput;
}

export interface FieldBucketDefinition {
  bucketName: string;
  minimumValue: number;
  maximumValue: number;
  includeMinimum: boolean;
  includeMaximum: boolean;
  color: RGBAColorInput;
}

export interface BreakdownOptions extends ScoringDisplayOptions {
  fieldId: string;
  numericMetadataFieldId: string;
  scoring: string;
  min?: number;
  max?: number;
  fieldBuckets?: FieldBucketDefinition[];
  isMetadataBinary?: boolean;
}

type BoxSize = '1' | '2' | '3';
type ComparisonColor = 'gray' | 'red' | 'green';

interface DonutOptions extends ScoringDisplayOptions {
  metric: 'avg' | 'sum' | 'box' | 'nps';
  metricBoxType?: 'top' | 'bottom';
  metricBoxSize?: BoxSize;
  minRating: number;
  maxRating: number;
  decimalPrecision: number;
  increaseColor: ComparisonColor;
  decreaseColor: ComparisonColor;
  showAs: 'absolute' | 'percentage';
  scoring: 'rating' | 'box';
}
interface GaugeOptions extends ScoringDisplayOptions {
  metric: MetricEnum;
  metricBoxType?: MetricBoxType;
  metricBoxSize?: BoxSize;
  minRating: number;
  maxRating: number;
  decimalPrecision: number;
  increaseColor: ComparisonColor;
  decreaseColor: ComparisonColor;
  showAs: MetricNumberType;
  scoring: MetricScoreType;
  axisValues: ChartAxisValues;
  min?: number;
  max?: number;
}

export enum MetricEnum {
  AVG = 'avg',
  SUM = 'sum',
  BOX = 'box',
  NPS = 'nps',
}

export enum MetricBoxType {
  TOP = 'top',
  BOTTOM = 'bottom',
}

export enum MetricNumberType {
  ABSOLUTE = 'absolute',
  PERCENTAGE = 'percentage',
}

export enum MetricScoreType {
  SCORING = 'scoring',
  RATING = 'rating',
}

export interface DateMarker {
  date: string;
  displayName?: string;
  color: RGBAColorInput;
}

@Component({
  selector: 'app-widget-chart',
  templateUrl: './widget-chart.component.html',
  styleUrls: ['./widget-chart.component.scss'],
  providers: [
    ChartAiInsightPointSelectionService,
    { provide: WidgetAiInsightsValidatorService, useClass: ChartWidgetAiInsightsValidatorService },
  ],
  standalone: false,
})
export class WidgetChartComponent<T extends WidgetChartOptions = WidgetChartOptions>
  extends AbstractWidgetComponent<T>
  implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
  canFilter = true;
  hasAnalyses = true;

  public widgetContextMenu: any;
  public report: AbstractChartReport;
  public downloadFormats = ['csv', 'pptx', 'image'];
  public originalGraphType: string; //DASH-1979

  displayedColumns: string[] = ['name', 'count'];
  dataTableColumnHeaders: Record<string, string> = {};
  analyticItems: string[] = ['documents', 'topics', 'entities', 'phrases', 'themes', 'taxonomy'];
  dataSource = [];

  public minDateInterval?: ChartDateInterval;
  public maxDateInterval?: ChartDateInterval;
  public prevSelectedDateInterval;
  point;
  pointNameForColorChange: string;
  selectedItemColor: RGBAColorInput | string;
  downloadingSpoteRequest?: boolean;
  downloadSmartSummaryRequestToolTip: string;

  customSortOrder: { name; label }[] = [];
  sortButtonProps = {
    value: {
      icon: undefined,
      tooltip: undefined,
    },
    label: {
      icon: undefined,
      tooltip: undefined,
    },
  };

  highchartsOptions: HighchartsOptions;
  isClusterExpanded = false;
  private fullTimelineButtonGroup: Highcharts.SVGElement;
  private expandedRangeOverlayGroup: Highcharts.SVGElement;
  private journeyDrillStack: JourneyDrillLevel[] = [];
  private currentJourneyDrillLevel: JourneyDrillLevel | null = null;
  private journeyDrillDateFilterValue: string | null = null;
  private isJourneyDrillInProgress = false;
  /** Buckets a probe proved undrillable, so clicking them lists their documents right away. */
  private unsplittableJourneyBucketIds = new Set<string>();

  hasDocFilterGroups: boolean;
  canAddAsParentFilter: boolean;
  showAddToWidgetGroupModal = false;
  canAddFilterToWidgetGroup;
  activePeriodParam: {
    first: 'previousStart' | 'currentStart';
    last: 'previousEnd' | 'currentEnd';
  } = { first: 'currentStart', last: 'currentEnd' };
  activeComparisonBucketIdx = 0;
  isAlreadyRefreshing = false;
  private pendingWidgetRefresh = false;

  itemColorChange$ = new Subject<RGBAColor>();
  isSentimentBased: boolean;

  reportExtra: { [key: string]: any };
  projectColors: { color: RGBAColor }[] = DEFAULT_PROJECT_COLOR_PALETTE.map((colorString) => ({
    color: ChartCustomColorService.colorStringToRgbaColor(colorString),
  }));
  pointIsTotalVolume = false;
  hasPointColorOverride = false;

  @Input() isCustomSortMode;
  @Input() public showGoBackButton = false;
  @Input() dashboard: Dashboard;
  /**
   * When provided, the chart skips its own API call and uses this response directly.
   * Used by the journey widget to share a single report response across the NMOT and timeline charts.
   */
  @Input() preloadedReportResponse: any;

  @Output() goBackClicked = new EventEmitter<void>();
  @Output() isCustomSortModeChange = new EventEmitter<boolean>();
  @Output() syncItemColors = new EventEmitter();
  @Output() resetItemColor = new EventEmitter<string>();
  @Output() reportResponseReady = new EventEmitter<any>();
  @Output() chartRendered = new EventEmitter<void>();
  @Output() xAxisZoomChanged = new EventEmitter<JourneyDrillRange | null>();

  @ViewChild(MatMenuTrigger, /* TODO: add static flag */ {})
  contextMenuTriggerElement: MatMenuTrigger;
  @ViewChild(MatMenuTrigger, /* TODO: add static flag */ { read: ElementRef })
  contextMenuTriggerNativeElement: ElementRef;
  @HostListener('window:scroll', [])
  onScroll() {
    if (this.contextMenuTriggerElement && this.contextMenuTriggerElement.menuOpen) {
      this.contextMenuTriggerElement.closeMenu();
    }
  }

  @ViewChild('widgetChartColorPicker') widgetChartColorPicker: ElementRef;
  destroy$ = new Subject<void>();
  constructor(
    protected documentService: DocumentService,
    protected chartReportService: ChartReportService,
    protected chartDisplayService: ChartDisplayService,
    protected chartCommentsService: ChartCommentsService,
    protected chartPointSelectionService: ChartAiInsightPointSelectionService,
    protected chartCustomColorService: ChartCustomColorService,
    protected chartTableService: ChartTableService,
    protected systemService: SystemService,
    protected filterService: FilterService,
    protected widgetLimiterService: WidgetLimiterService,
    protected alertService: AlertService,
    protected dialogService: DialogService,
    protected projectService: ProjectService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected logger: LoggerService,
    protected accountService: StorageAccountService,
    protected lexDatePipe: LexDatePipe,
    protected cpppService: CpppCalculationService,
    protected authService: AuthService,
    protected downloadService: DownloadTasksService,
    protected comparisonService: ComparisonHandlerService,
    protected timezoneService: TimezoneService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.prevSelectedDateInterval = undefined;
    this.isLoading = true;

    this.itemColorChange$
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
        // distinctUntilChanged prevents us from changing color more than 1 time on the same chart item,
        // if we need bahavior like that we need to figure out a different solution. Not needed at the moment
      )
      .subscribe((value) => this.onItemColorChange(value));

    if (this.widget.payload.type === 'numericMetadata' && this.widget.payload.newspaper?.show) {
      this.minWidth = 6;
    }

    if (
      this.widget.payload.type === 'taxonomyMap' &&
      this.widget.component === 'DashboardWidgetChartComponent'
    ) {
      this.widget.component = 'WidgetTaxonomyMapComponent';
    }
    //DASH-3737
    if (
      this.widget.payload.type === 'numericMetadata' ||
      this.widget.payload.type === 'featureScoring' ||
      this.widget.payload.type === 'sentimentAccuracy'
    ) {
      this.widget.payload.customFieldSelected = true;
    }
    super.updateFilterList();

    this.isSentimentBased = [
      'sentimentVolume',
      'sentimentPolarity',
      'wordcloud',
      'taxonomyMap',
    ].includes(this.widget.payload.type);
    this.widget = this.chartCustomColorService.initializeCustomColorsProperty(this.widget);
    this.maxDateInterval = 'year';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.widgetDataSource && this.report) {
      this.report.options.widgetDataSource = this.widgetDataSource;
    }
    if (changes.project?.currentValue?.colorPalette) {
      this.projectColors = this.project.colorPalette.map((colorString) => ({
        color: ChartCustomColorService.colorStringToRgbaColor(colorString),
      }));
    }
    const shouldRefresh =
      (changes.widget && !changes.widget.firstChange && changes.widget.currentValue) ||
      (changes.preloadedReportResponse && !changes.preloadedReportResponse.firstChange);
    if (shouldRefresh) {
      if (this.isAlreadyRefreshing) {
        // A refresh triggered by an earlier widget change (e.g. initial load for a large
        // dataset) is still in flight. Queue this update so it is not silently dropped;
        // otherwise the chart can end up rendering stale, unfiltered data indefinitely.
        this.pendingWidgetRefresh = true;
      } else {
        this.refresh(false, false);
      }
    }
  }

  ngAfterViewInit() {
    this.chartReportService.fieldTypeMapPromise.then(() => this.refresh(false, true));
    this.contextMenuTriggerElement?.menuClosed.subscribe(
      () => (this.widgetContextMenu = undefined)
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.itemColorChange$?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.widgetLoadController.abort('destroyed');
  }

  get canChangeItemColor() {
    if (this.isSentimentBased) {
      return false;
    }
    if (
      (this.payload.type === 'donut' && this.payload.donutOptions.scoring === 'box') ||
      this.payload.type === 'gauge'
    ) {
      return false;
    }
    if (this.payload.type === 'breakdown' && this.payload.breakdownOptions.scoring === 'box') {
      return false;
    }
    if (this.payload.type === 'numericMetadata') {
      return false;
    }
    return true;
  }

  get canApplyChartColors() {
    return (
      this.canChangeItemColor || (this.payload.type === 'numericMetadata' && !!this.comparison)
    );
  }

  get defaultTitle(): string {
    let selectedItemName = this.widget.payload.selectedItems?.[0];
    if (this.widget.payload.type === 'sentimentVolume') {
      selectedItemName = this.report && (this.report as any).getSelectedItem();
      selectedItemName = Utils.unescapeHtml(selectedItemName);
    }

    return WidgetUtility.getWidgetTitle(this.widget, { selectedItemName });
  }

  get customType() {
    return this.fieldDetailsMap.get(this.widget.payload.analyticItem)?.fieldType;
  }

  isAnalyticItemOfCustomType(type) {
    return this.widget && this.widget.payload.customFieldSelected && this.customType === type;
  }

  private isAnalyticItemCustomNumeric() {
    return this.isAnalyticItemOfCustomType('double') || this.isAnalyticItemOfCustomType('long');
  }

  get noData(): boolean {
    return !this.isLoading && this.dataSource.length === 0;
  }

  get noDocuments(): boolean {
    return !this.isLoading && this.filteredDocumentCount === 0;
  }

  get isChangeColorDisabled(): boolean {
    return (
      this.comparison &&
      this.comparison.filters.length > 1 &&
      this.widget.payload.type === 'documentCount' &&
      (this.widget.payload.graphType === 'bar' || this.widget.payload.graphType === 'column')
    );
  }

  get canDownload(): boolean {
    if (this.hasInvalidComparison) {
      return false;
    }

    return super.canDownload;
  }

  canCreateCollection(): boolean {
    if (this.hasInvalidComparison) {
      return false;
    }

    return super.canCreateCollection();
  }

  async exportAsImage(format: 'blob' | 'canvas' = 'blob') {
    const widgetTitle = this.defaultTitle;
    if (format === 'canvas') {
      return toCanvas(this.elementContent.nativeElement);
    }

    return await this.chartDisplayService.exportChartAsImage(
      this.elementContent.nativeElement,
      widgetTitle,
      format
    );
  }

  protected addSlideOptions(dataOptions: any) {
    super.addSlideOptions(dataOptions);
    dataOptions.isCustomDate = this.isAnalyticItemOfCustomType('date');
    dataOptions.isCustomDouble = this.isAnalyticItemOfCustomType('double');
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    const widgetTitle =
      !this.widget.title && this.widget.title !== '' ? this.widget.title : this.defaultTitle;

    this.addTitleSlideToPptx(pptx);

    if (this.report.canExportAsPptx) {
      const chartObject = this.chartDisplayService.getChart(this.elementContent.nativeElement);
      const series = chartObject.userOptions.series.map((s, i) => ({
        ...s,
        data: chartObject.series[i].data,
      }));
      const dataOptions = { ...chartObject.userOptions, series };
      this.addSlideOptions(dataOptions);

      if (this.noDocuments || this.noData) {
        const text = this.noDocuments
          ? translate('widgets.no-documents-found')
          : translate('widgets.no-data-found');

        pptx.addTextSlide({
          title: widgetTitle,
          text,
          data: dataOptions,
          sentimentScale: this.widget.payload.sentimentScale.toString(),
        });
      } else {
        const getPptxOverride = () => {
          if (
            this.widget.payload.aggregator === 'percentage' &&
            this.widget.payload.type === 'sentimentPolarity'
          ) {
            return {
              barGrouping: this.widget.payload.showAsStacked ? 'percentStacked' : 'clustered',
              overrideDataReverse: this.widget.payload.showAsStacked,
            };
          }
          if (
            this.widget.payload.type === 'breakdown' &&
            this.widget.payload.breakdownOptions.scoring === 'box'
          ) {
            return { overrideDataReverse: true };
          }
          return null;
        };
        // This is only needed for sentimentPolarity Export
        const pptxOverride = getPptxOverride();
        pptx.addHighchartSlide(
          {
            title: widgetTitle,
            data: dataOptions,
            sentimentScale: this.widget.payload.sentimentScale.toString(),
          },
          {
            pptxOverride,
          }
        );
      }
      if (this.widget.payload?.newspaper?.show) {
        pptx.addSnapshotAsTextSlide({
          title: widgetTitle,
          snapshotData: this.getSnapshotText(),
          data: dataOptions,
          sentimentScale: this.widget.payload.sentimentScale.toString(),
        });
      }
    } else {
      await this.exportChartToPptx(pptx, widgetTitle);
    }
  }

  private getSnapshotText(): SnapshotSlideObject {
    const snapShotData: SnapshotSlideObject = {
      title: '',
      writtenAt: '',
      story: {
        heading: '',
        contents: [],
      },
    };

    snapShotData.title = translate('widgets.newspaper-widglet.header');
    snapShotData.writtenAt = translate('widgets.newspaper-widglet.subheader', {
      date: this.lexDatePipe.transform(
        new Date(this.widget.payload.newspaper.generatedAt),
        'yyyy-MM-DD HH:mm:ss'
      ),
    });
    const { stories } = this.widget.payload.newspaper;
    snapShotData.story.heading = stories[0].heading;
    for (const text of stories[0].contents) {
      snapShotData.story.contents.push({
        subHeader: text.subHeader.replace('<b>', '').replace('</b>', ''),
        insight: text?.insight ? text?.insight?.replace('\n\n', '\n') : '',
      });
    }
    return snapShotData;
  }

  protected async exportChartToPptx(pptx, widgetTitle, imageOptions?, slide?) {
    const blob = await this.chartDisplayService.exportChartAsImage(
      this.elementContent.nativeElement
    );
    return await this.exportImageToPptx(blob, pptx, widgetTitle, imageOptions, slide);
  }

  public async exportAsCsv() {
    if (this.payload.type === 'breakdown') {
      const params = await this.report.getParams();
      params['breakdownLabel'] = FieldService.getFieldLongName(params['breakdownField']);
      const mapData = this.report.getMapData(true);
      for (const entry of mapData) {
        if (entry['scoringStrategy'] === 'BOX') {
          entry['topLabel'] = 'Top';
          entry['neutralLabel'] = 'Neutral';
          entry['bottomLabel'] = 'Bottom';
        }
      }
      if (!mapData.some((element) => element.customField === 'Documents')) {
        // if the report doesn't ask for a documents column, add one
        const docs = {
          label: translate('widgets.documents'),
          scoringStrategy: 'COUNT',
          customField: 'Documents',
        };
        mapData.splice(0, 0, docs);
      }

      const payload = {
        _type: TaskType.REPORT_EXTRACTOR_REPORT,
        filename: 'breakdown-report',
        source: this.dashboard.title,
        projectId: this.dashboard.project_id,
        collectionId: this.widgetDataSource.collectionName,
        analysisGroupId: this.widgetDataSource.groupId,
        reportName: 'documentNumericMetadata',
        query: this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true),
        params: encodeParams(params, this.widgetDataSource.groupId),
        mapData,
      };
      try {
        await this.downloadService.createTask(payload);
        this.alertService.showToast(
          translate('widgets.download-being-generated'),
          undefined,
          undefined,
          undefined,
          'open-download-center-pointer',
          'open-download-center'
        );
      } catch (error) {
        this.alertService.showErrorToast(translate('widgets.failed-to-download'), error);
      }
      return;
    } else if (this.payload.type === 'numericMetadata' && this.comparison?.filters.length > 1) {
      const data = this.exportNmotWithComparisonWith2orMoreBucket();

      return { data };
    }

    const queryForDocuments = this.filterService.getQueryForDocuments(
      this.widgetQueryOptions,
      false,
      true
    );

    const data = await this.executeReport(
      'csv',
      this.widget.payload.drilledDownOn,
      queryForDocuments
    );
    return { data };
  }

  private exportNmotWithComparisonWith2orMoreBucket(): string {
    const sb = new StringBuilder();

    const maxBucketLength = this.dataSource.reduce(
      (max, curr) => Math.max(max, curr.buckets.length),
      0
    );

    const isOnlyDocs =
      this.widget.payload.numericMetadataFields.length === 1 &&
      this.widget.payload.numericMetadataFields[0].fieldId === 'docs';

    const headerBucketPart = 'count' + (isOnlyDocs ? '' : ',score');

    const header = `date,name,${Array(maxBucketLength).fill(headerBucketPart).join(',')}`;

    sb.appendLine(header);

    const dateColumn = this.widget.payload.dateInterval.toString();

    const getBucketValues = (
      buckets: { count: number; score: number }[],
      bucketLen: number
    ): string => {
      const values: number[] = [];

      for (let index = 0; index < bucketLen; index++) {
        const bucket = buckets[index];

        values.push(bucket?.count ?? 0);

        if (!isOnlyDocs) {
          values.push(bucket?.score ?? 0);
        }
      }

      return values.join(',');
    };

    this.dataSource.forEach(
      (row: { metadata: string; buckets: { count: number; score: number }[] }) => {
        sb.appendLine(
          `${Utils.escapeCsvString(row[dateColumn])},${Utils.escapeCsvString(
            row.metadata
          )},${getBucketValues(row.buckets, maxBucketLength)}`
        );
      }
    );

    return sb.toString();
  }

  async refresh(resetUserDateInterval = false, initialLoad = false): Promise<any> {
    if (this.isAlreadyRefreshing) {
      return;
    }

    this.isAlreadyRefreshing = true;
    this.isLoading = true;
    this.loadingStarted.emit(initialLoad);

    if (this.dataSourceNotFound || !this.widgetDataSource) {
      this.loadingFinished.emit(initialLoad);
      this.isLoading = false;
      this.finishRefresh();
      return;
    }

    this.widget.payload.sentimentScale = this.dashboard.payload.sentimentScale;
    this.widget.payload.projectId = this.dashboard.project_id || null;

    this.widgetComponent.updateTemplateBooleans();

    this.sortButtonProps = {
      label: {
        icon: undefined,
        tooltip: undefined,
      },
      value: {
        icon: undefined,
        tooltip: undefined,
      },
    };

    return this.widgetLimiterService.execute(async () => {
      try {
        if (this.widgetLoadController.signal.aborted) {
          return;
        }

        if (this.hasInvalidComparison) {
          return;
        }

        const reportOptions = await this.cpppService.assembleReportOptions(
          this.widget.payload,
          this.widgetDataSource,
          this.widgetGroup
        );

        reportOptions.widgetDataSource = this.widgetDataSource;
        reportOptions.comparisonSectionFilters = FilterUtils.comparisonBucketToSelectedSections(
          this.comparison,
          0
        );

        this.report = this.chartReportService.configureReport(reportOptions);

        if (resetUserDateInterval) {
          this.widget.payload.userSelectedDateRange = false;
        }

        this.minDateInterval = this.calculateDateInterval(); //Gets the minimum date interval
        if (reportOptions.widgetDataSource.isSocial) {
          this.maxDateInterval = 'month';
        }
        if (this.report.getFields().includes('dateInterval')) {
          const intervalBefore = this.widget.payload.dateInterval;

          // Journey timeline/NMOT charts set the interval programmatically for every drill level and
          // always add the matching bucket date filter, so the user-selection clamps don't apply.
          if (!this.isJourneyDrillChart) {
            this.widget.payload.dateInterval = this.isDateIntervalEnabled(
              this.widget.payload.dateInterval
            ) //if the interval is enabled, we use the date interval, otherwise we use the previously selected one.
              ? this.widget.payload.dateInterval
              : this.minDateInterval;

            if (
              this.prevSelectedDateInterval !== undefined &&
              this.prevSelectedDateInterval !== null
            ) {
              //the prev date is earlier than the min date.
              if (
                ChartDateInterval.indexOf(this.minDateInterval) >
                ChartDateInterval.indexOf(this.prevSelectedDateInterval)
              ) {
                this.widget.payload.dateInterval = this.minDateInterval; //we can't send the previous interval anymore, so we send the minimum.
              }
            }
          }

          this.report.options.dateInterval = this.widget.payload.dateInterval;
          this.prevSelectedDateInterval = this.widget.payload.dateInterval;
          if (this.widget.payload.dateInterval !== intervalBefore) {
            this.dashboardStore.markDirty();
          }
        }

        let response: any = {};

        try {
          const cpResponse = await this.calculateCurrentPeriod(reportOptions);
          if (this.report.lastDocumentCount !== undefined) {
            this.filteredDocumentCount = this.report.lastDocumentCount;
            this.report.options.filteredDocumentCount = this.filteredDocumentCount;
          }

          this.activePeriodParam.first = 'previousStart';
          this.activePeriodParam.last = 'previousEnd';

          this.activeComparisonBucketIdx = 0;

          if (this.widgetGroup?.isImpact) {
            this.report.options.previousPeriodDocumentCount = this.report.lastDocumentCount;
          }

          const reportResponse = this.preloadedReportResponse
            ? this.preloadedReportResponse
            : await this.executeReport(
                'json',
                this.widget.payload.drilledDownOn,
                this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true)
              );
          if (reportResponse.totalHits !== undefined) {
            this.report.lastDocumentCount = reportResponse.totalHits;
          }
          response = {
            ...reportResponse,
            ...cpResponse,
          };

          this.reportExtra = response.extra;

          if (
            response?.items.metric?.score !== undefined &&
            (response?.items?.metric?.score === null ||
              response?.items?.metric?.score === 'Infinity' ||
              response?.items?.metric?.score === 'NaN')
          ) {
            this.filteredDocumentCount = 0;
            this.report.options.filteredDocumentCount = this.filteredDocumentCount;
            this.isLoading = false;
            this.loadingFinished.emit(true);
            this.isAlreadyRefreshing = false;
          }

          if (this.report.lastDocumentCount !== undefined) {
            this.filteredDocumentCount = this.report.lastDocumentCount;
            this.report.options.filteredDocumentCount = this.filteredDocumentCount;
          }

          if (this.comparison?.filters.length) {
            response.comparisonBuckets = [{ ...response }];

            this.report.options.comparisonBucketsCounts = [this.report.lastDocumentCount];
            await this.calculateComparisonBuckets(response, reportOptions);
          }

          this.activePeriodParam.first = 'currentStart';
          this.activePeriodParam.last = 'currentEnd';

          this.activeComparisonBucketIdx = 0;

          this.reportResponseReady.emit(response);
        } catch (error) {
          this.logger.error('Error during chart initialization', error);
          if (error.status === 404) {
            this.dataSourceNotFound = true;
          }
          this.isLoading = false;
          this.loadingFinished.emit(initialLoad);
          this.isAlreadyRefreshing = false;
          return;
        }

        if (this.widgetLoadController.signal.aborted) {
          return;
        }

        this.dataSourceNotFound = false;
        this.highchartsOptions = this.report.configureChart(this.dashboard, this.widget, response);
        this.resetJourneyDrillState();

        if (initialLoad) {
          this.chartCustomColorService.updateDictionaryItems(this.widget);

          this.interactiveSmartSummaryService
            .getSmartSummaries(this.dashboard.project_id, this.dashboard.id, this.widget.id)
            .then((res) => {
              this.storedSmartSummaries = res;
              this.isSmartSummaryGenerating = this.storedSmartSummaries.some(
                (sum) => sum.status === 'RUNNING'
              );
              if (this.isSmartSummaryEnabled) {
                this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
                this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
              }
            });
        }

        this.highlightBluePill();

        this.chartCustomColorService.addChartOptionsToDictionary(
          this.widget.id,
          this.highchartsOptions
        );

        if (this.canApplyChartColors) {
          this.widget.payload.customItemColors =
            this.chartCustomColorService.getItemColorsFromDictionary(this.widget);

          this.highchartsOptions = this.chartCustomColorService.setChartItemColors(
            this.widget,
            this.highchartsOptions
          );

          this.highchartsOptions = this.comparisonService.setChartColors(
            this.widget,
            this.comparison,
            this.highchartsOptions
          );
        }

        let eventBaseObjects = [this.highchartsOptions];
        let eventProp = 'plotOptions.series.point.events.click';
        if (this.payload.type === 'gauge') {
          const yAxisArr = Array.isArray(this.highchartsOptions.yAxis)
            ? this.highchartsOptions.yAxis
            : [this.highchartsOptions.yAxis];
          eventBaseObjects = yAxisArr.flatMap((axis) => axis.plotBands ?? []);
          eventProp = 'events.click';
        } else if (this.widget.payload.graphType === 'timeline') {
          eventProp = 'plotOptions.timeline.point.events.click';
        }

        const self = this;
        for (const [idx, eventObj] of eventBaseObjects.entries()) {
          Object.setNestedPropertyValue(eventObj, eventProp, async function (e) {
            if (!self.contextMenuTriggerElement) {
              return;
            }

            if (!e.point) {
              if (self.widget.payload.graphType === 'timeline') {
                e.point = this;
              } else {
                e.point = this.axis.series[0].data[0].point;
              }
              e.chartX = e.chartX ?? e.clientX;
              e.chartY = e.chartY ?? e.clientY;
            }
            if (self.isJourneyTouchpointsChart) {
              if (
                self.canDrillIntoJourneyPoint(e.point) &&
                (await self.drillIntoJourneyPoint(e.point))
              ) {
                return;
              }

              self.preparePointContext(e);
              await self.showDocumentsForItem();
              return;
            }

            e.point.eventIndex = idx;

            self.widgetContextMenuOpen(e);
          });
        }

        if (resetUserDateInterval || !this.widget.payload.legends) {
          this.widget.payload.legends = {};
        }

        if (!this.highchartsOptions.chart?.events?.render) {
          Object.setNestedPropertyValue(
            this.highchartsOptions,
            'chart.events.render',
            function (e: HighchartsRenderEvent) {
              const realSeries = e.target.series.filter((s) => !s.userOptions.custom?.seriesType);

              for (const series of realSeries) {
                const isVisibilityChanged =
                  (self.widget.payload.legends[series.name] ?? true) !== series.visible;

                self.widget.payload.legends[series.name] = series.visible;

                if (isVisibilityChanged) {
                  let comments = self.payload.comments ?? [];
                  if (realSeries.length > 1) {
                    comments = self.report.getCommentsForSeries(
                      series,
                      self.payload,
                      self.comparison
                    );
                  }

                  for (const comment of comments) {
                    e.target['annotations']
                      .find((a) => a.options.id === comment.id)
                      ?.update({
                        visible: self.report.isCommentVisible(comment, self.widget, series),
                      });
                  }

                  self.commentsHandlerService.setVisibleComments(self.getVisibleComments());
                }
              }
            }
          );
        }

        const renderFn = this.highchartsOptions.chart.events.render;

        this.highchartsOptions.chart.events.render = function (e) {
          renderFn.call(this, e);
          if (self.widgetComponent.canAddComment) {
            self.widgetComponent.updateWidgetActionComments();
          }
          self.chartRendered.emit();
        };

        if (this.highchartsOptions.xAxis?.events?.afterSetExtremes) {
          this.highchartsOptions.xAxis.events.afterSetExtremes = (event) => {
            ChartDataService.setZoom(this.widget, this.dashboard.payload.timezone, event);
            this.dashboardStore.markDirty();
            setTimeout(() => this.widgetComponent.pillWrapper.checkCompactSize(), 10);
          };
        }

        this.highchartsOptions.annotations?.forEach((a) => {
          a.events = {
            click: this.annotationClickHandlerFn(),
            afterUpdate: this.annotationDragHandlerFn(),
          };
        });

        this.highchartsOptions.isCustomDate = this.isAnalyticItemOfCustomType('date');
        this.highchartsOptions.isCustomDouble = this.isAnalyticItemOfCustomType('double');
        this.highchartsOptions.isCustomNumeric = this.isAnalyticItemCustomNumeric();

        this.highchartsOptions = this.formatHighchartsData(this.highchartsOptions);
        const isChartSortable = !response.currentPeriodData && this.canSort;
        this.chartDisplayService.drawChart(
          this.elementContent.nativeElement,
          this.dashboard,
          this.widget,
          response,
          this.highchartsOptions,
          isChartSortable
        );

        this.createDataTable(response);

        this.hasDocFilterGroups = this.dashboard.widgetGroups
          ? Object.getOwnPropertyNames(this.dashboard.widgetGroups).length > 0
          : false;

        this.canAddAsParentFilter =
          this.hasDocFilterGroups &&
          (this.widget.payload.type !== 'documentCount' ||
            this.widget.payload.analyticItem !== 'documents' ||
            ChartOptionUtils.isOverTimeAggregator(this.widget.payload.aggregator));
      } finally {
        this.isLoading = false;
        this.loadingFinished.emit(initialLoad);
        this.finishRefresh();
      }
    });
  }

  private finishRefresh(): void {
    this.isAlreadyRefreshing = false;
    if (this.pendingWidgetRefresh) {
      this.pendingWidgetRefresh = false;
      this.refresh(false, false);
    }
  }

  protected createDataTable(
    response,
    missing = 0,
    previousMissing = 0,
    comparisonMissings?: Map<number, number>,
    options?: { [key: string]: any }
  ) {
    const dataTable = this.chartTableService.tranformToDataSource(
      this.dashboard,
      this.widget,
      response,
      options,
      this.report,
      missing,
      previousMissing,
      comparisonMissings
    );

    if (typeof dataTable !== 'undefined') {
      this.dataSource = dataTable.rows;
      this.displayedColumns = dataTable.columns;
      this.dataTableColumnHeaders = dataTable.options?.columnHeaders ?? {};
      if (this.widget.payload.aggregator === 'percentage') {
        this.displayedColumns = dataTable.columns.map((c) => (c === 'name' ? c : c + '_pct'));

        for (const col of this.displayedColumns.slice()) {
          if (col.match(/count.*_pct/)) {
            this.displayedColumns.splice(
              this.displayedColumns.indexOf(col) + 1,
              0,
              col.replace(/_pct$/, '')
            );
          }
        }
      }
    }
  }

  protected async calculateCurrentPeriod(_reportOptions): Promise<any> {
    return {};
  }

  protected async calculateComparisonBuckets(response, _reportOptions) {
    while (this.activeComparisonBucketIdx++ < this.comparison.filters.length - 1) {
      // DASH-9694: When activeComparisonBucketIdx changes we must recalculate comparisonSectionFilters for the active bucket.
      this.report.options.comparisonSectionFilters = FilterUtils.comparisonBucketToSelectedSections(
        this.comparison,
        this.activeComparisonBucketIdx
      );

      const widgetQueryOptions = this.widgetQueryOptions;
      const bucketResponse = await this.executeReport(
        'json',
        this.widget.payload.drilledDownOn,
        this.filterService.getQueryForDocuments(widgetQueryOptions, false, true)
      );

      response.comparisonBuckets.push(bucketResponse);
      this.report.options.comparisonBucketsCounts.push(this.report.lastDocumentCount);
      this.filteredDocumentCount += this.report.lastDocumentCount;
    }
  }

  protected formatHighchartsData(highchartsOptions: HighchartsOptions) {
    return highchartsOptions;
  }

  protected async executeReport(
    format: 'csv' | 'json',
    drillDown?: WidgetChartOptions['drilledDownOn'],
    queryForDocuments?: string
  ) {
    let response;

    switch (drillDown) {
      case 'mentions':
        response = await this.report.executeReportForMentions(
          this.widget.payload.drilledDownLocation,
          format,
          queryForDocuments
        );
        break;
      case 'topic_phrases':
        response = await this.report.executeReportForFeaturePhrases(
          this.widget.payload.drilledDownLocation,
          'topics',
          format,
          queryForDocuments,
          undefined
        );
        break;
      case 'entity_phrases':
        response = await this.report.executeReportForFeaturePhrases(
          this.widget.payload.drilledDownLocation,
          'entities',
          format,
          queryForDocuments,
          undefined
        );
        break;
      case 'themes':
        response = await this.report.executeReportForThemes(
          this.widget.payload.drilledDownLocation,
          format,
          queryForDocuments
        );
        break;
      default:
        response = await this.report.executeReport(format, queryForDocuments);
    }

    return response;
  }

  redraw() {
    this.chartDisplayService.reflowChart(this.elementContent.nativeElement);
  }

  public getPlotAreaMargins(): { left: number; right: number } | null {
    const chart = this.chartDisplayService.getChart(this.elementContent.nativeElement);
    if (!chart) {
      return null;
    }
    return {
      left: chart.plotLeft,
      right: Math.max(chart.chartWidth - chart.plotLeft - chart.plotWidth, 0),
    };
  }

  public syncPlotAreaFrom(sourceChartComponent: WidgetChartComponent): void {
    const sourceMargins = sourceChartComponent.getPlotAreaMargins();
    const chart = this.chartDisplayService.getChart(this.elementContent.nativeElement);
    if (!sourceMargins || !chart) {
      return;
    }
    const currentRight = Math.max(chart.chartWidth - chart.plotLeft - chart.plotWidth, 0);
    if (chart.plotLeft === sourceMargins.left && currentRight === sourceMargins.right) {
      return;
    }
    this.highchartsOptions.chart = {
      ...this.highchartsOptions.chart,
      marginLeft: sourceMargins.left,
      marginRight: sourceMargins.right,
    };
    chart.update(
      {
        chart: {
          marginLeft: sourceMargins.left,
          marginRight: sourceMargins.right,
        },
      },
      true,
      false
    );
  }

  setXAxisExtremes(range: { min: number; max: number } | null): void {
    const chart = this.chartDisplayService.getChart(this.elementContent.nativeElement);
    if (!chart?.xAxis?.[0]) {
      return;
    }
    if (range) {
      chart.xAxis[0].setExtremes(range.min, range.max, true, false);
      this.updateExpandedRangeOverlay(chart, range);
    } else {
      chart.xAxis[0].setExtremes(undefined, undefined, true, false);
      this.clearExpandedRangeOverlay();
    }
  }

  resetZoom() {
    this.widgetComponent.resetZoom();
  }

  get isZoomedIn() {
    return this.widget.payload.zoomDateFilter;
  }

  get isJourneyTouchpointsChart(): boolean {
    return (this.widget.payload as any).journeyTimelineRole === 'touchpoints';
  }

  get isJourneyDrillChart(): boolean {
    const role = (this.widget.payload as any).journeyTimelineRole;
    return role === 'touchpoints' || role === 'nmot';
  }

  public get parentPointName(): string {
    if (this.widgetContextMenu && this.widgetContextMenu.parent) {
      const parentNode = this.widgetContextMenu.series.data.find(
        (item) => item.id === this.widgetContextMenu.parent
      );
      return parentNode.name;
    }
    return undefined;
  }

  public get canHideItems(): boolean {
    if (this.widget.component === 'DashboardWidgetSentimentAccuracyComponent') {
      return false;
    }

    return this.report.canHideItems();
  }

  public get canDrilldown(): boolean {
    if (this.report) {
      if (this.widget.component === 'DashboardWidgetSentimentAccuracyComponent') {
        return false;
      } else if (
        this.payload.isDrilledDown &&
        (this.payload.analyticItem === 'topics' || this.payload.analyticItem === 'entities')
      ) {
        return false;
      }
      return this.report.canDrilldown();
    }
    return false;
  }

  public hasOccurences(): boolean {
    return (
      this.payload.analyticItem !== 'documents' &&
      !this.payload.customFieldSelected &&
      !this.payload.standardFieldSelected
    );
  }

  public get canDrilldownMentions(): boolean {
    if (this.report) {
      const result = this.report.canDrilldownMentions();
      if (this.payload.analyticItem !== 'topics' && this.payload.analyticItem !== 'entities') {
        return false;
      }
      return result;
    }
    return false;
  }

  public get canDrilldownThemes(): boolean {
    const result = this.report.canDrilldownThemes();
    if (this.payload.analyticItem !== 'entities' && this.payload.analyticItem !== 'topics') {
      return false;
    }
    return result;
  }

  public get canShowDocuments(): boolean {
    if (this.hasInvalidComparison) {
      return false;
    }

    const canShowDocuments =
      this.widget.payload.type !== 'sentimentPolarity' ||
      this.widgetContextMenu?.series?.name !== 'Total volume';

    const hasDocuments =
      (this.widgetContextMenu && this.widgetContextMenu.y !== 0) ||
      (this.widgetContextMenu &&
        Number(this.widgetContextMenu.clusterSize ?? this.widgetContextMenu.documentCount ?? 0) !==
          0);

    return canShowDocuments && hasDocuments;
  }

  /** Date interval the journey timeline is currently displayed with. */
  public get journeyDrillInterval(): string {
    return this.currentJourneyDrillLevel?.dateInterval ?? this.widget.payload.dateInterval;
  }

  private canDrillIntoJourneyPoint(point: Highcharts.Point): boolean {
    const { documentCount, id } = point.options as { documentCount?: number; id?: string };
    return (
      documentCount > 1 &&
      !this.isAlreadyRefreshing &&
      !this.unsplittableJourneyBucketIds.has(id) &&
      !!ChartDataService.getNextJourneyTimelineDateInterval(this.journeyDrillInterval)
    );
  }

  /**
   * Drills into the clicked bucket and reports whether a drilled level is now displayed.
   * A single probe at the finest interval tells which coarser intervals would separate the bucket,
   * so levels that would render the same single marker are never loaded. Documents sharing a
   * timestamp cannot be separated at all, in which case the caller lists them instead.
   */
  private async drillIntoJourneyPoint(point: Highcharts.Point): Promise<boolean> {
    const previousDateInterval = this.journeyDrillInterval;
    const dateRange = this.getJourneyPointIntervalRange(point.x, previousDateInterval);
    const probeResponse = await this.probeJourneyBucket(dateRange);
    if (!probeResponse) {
      // The probe failed and the error was surfaced, so the click is done.
      return true;
    }
    const bucketTimestamps = Object.keys(probeResponse.items ?? {}).map((date) => Date.parse(date));
    const dateInterval = ChartDataService.getCoarsestSplittingDateInterval(
      bucketTimestamps,
      previousDateInterval,
      this.timezoneService.timezone
    );
    if (!dateInterval) {
      this.unsplittableJourneyBucketIds.add((point.options as { id?: string }).id);
      return false;
    }
    this.journeyDrillStack.push(
      this.currentJourneyDrillLevel ?? {
        dateInterval: previousDateInterval,
        dateRange: null,
        previousDateInterval: null,
      }
    );
    await this.applyJourneyDrillLevel(
      { dateInterval, dateRange, previousDateInterval },
      // The probe already queried this level, so its response is reused instead of repeating it.
      dateInterval === ChartDataService.JOURNEY_TIMELINE_FINEST_DATE_INTERVAL
        ? probeResponse
        : undefined
    );
    return true;
  }

  /**
   * Executes the report for a single bucket at the finest drill interval, without touching the
   * displayed level. Returns null when the call failed.
   */
  private async probeJourneyBucket(dateRange: { fromDate: Moment; toDate: Moment }): Promise<any> {
    const baseQuery = this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true);
    const rangeQuery = this.filterService.generateDateRangeQueryPart('createdAt', dateRange);
    const query = baseQuery ? `${baseQuery} AND ${rangeQuery}` : rangeQuery;
    const displayedDateInterval = this.report.options.dateInterval;
    this.report.options.dateInterval = ChartDataService.JOURNEY_TIMELINE_FINEST_DATE_INTERVAL;
    try {
      return await this.report.executeReport('json', query);
    } catch (error) {
      this.alertService.showErrorToast(
        translate('widgets.journey.error-loading-journey-report'),
        error
      );
      return null;
    } finally {
      this.report.options.dateInterval = displayedDateInterval;
    }
  }

  async restoreClusteredView(): Promise<void> {
    if (this.isAlreadyRefreshing) {
      return;
    }
    const previousLevel = this.journeyDrillStack.pop();
    if (!previousLevel) {
      return;
    }
    this.fullTimelineButtonGroup?.destroy();
    this.fullTimelineButtonGroup = undefined;
    this.clearExpandedRangeOverlay();
    this.setOverflow('hidden');
    await this.applyJourneyDrillLevel(previousLevel);
  }

  /**
   * Applies a journey drill level: the clicked bucket becomes a widget date filter and the report
   * is re-executed at that level's date interval, so the backend does the bucketing. A response
   * already fetched for that exact level can be passed in to skip the call.
   */
  private async applyJourneyDrillLevel(
    level: JourneyDrillLevel,
    loadedResponse?: any
  ): Promise<void> {
    this.currentJourneyDrillLevel = level.dateRange ? level : null;
    this.setJourneyDrillDateFilter(level.dateRange);
    this.widget.payload.dateInterval = level.dateInterval as ChartDateInterval;
    this.prevSelectedDateInterval = level.dateInterval;
    // The shared journey response is bucketed at the previous level, so unless the level has
    // already been loaded the drilled chart has to make its own report call.
    this.preloadedReportResponse = loadedResponse;
    this.isJourneyDrillInProgress = true;
    try {
      await this.refresh();
    } finally {
      this.isJourneyDrillInProgress = false;
    }
    this.isClusterExpanded = this.journeyDrillStack.length > 0;
    this.xAxisZoomChanged.emit(this.getJourneyDrillRange(level));
  }

  private getJourneyDrillRange(level: JourneyDrillLevel): JourneyDrillRange | null {
    if (!level.dateRange) {
      return null;
    }
    const dataMin = level.dateRange.fromDate.valueOf();
    const dataMax = level.dateRange.toDate.valueOf();
    // Both journey charts must share the axis window the drilled timeline ended up with.
    const axis = this.highchartsOptions?.xAxis as { min?: number; max?: number } | undefined;
    return {
      min: axis?.min ?? dataMin,
      max: axis?.max ?? dataMax,
      dataMin,
      dataMax,
      dateInterval: level.dateInterval,
      previousDateInterval: level.previousDateInterval,
      dateFilter: this.journeyDrillDateFilterValue,
    };
  }

  private setJourneyDrillDateFilter(dateRange: { fromDate: Moment; toDate: Moment } | null): void {
    const dateFilters = this.widget.payload.filters.dates.and;
    if (this.journeyDrillDateFilterValue) {
      const idx = dateFilters.findIndex(
        (filter) => filter.value === this.journeyDrillDateFilterValue
      );
      if (idx !== -1) {
        dateFilters.splice(idx, 1);
      }
      this.journeyDrillDateFilterValue = null;
    }
    if (!dateRange) {
      return;
    }
    this.journeyDrillDateFilterValue = `daterange/${dateRange.fromDate.format(
      DATE_TIME_WITHOUT_TIMEZONE_FORMAT
    )}/${dateRange.toDate.format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT)}`;
    dateFilters.push({
      analyticItem: 'createdAt',
      key: 'daterange',
      value: this.journeyDrillDateFilterValue,
    });
  }

  private addFullTimelineButton(chart: Highcharts.Chart): void {
    this.fullTimelineButtonGroup?.destroy();
    const { renderer, chartWidth, chartHeight, plotHeight } = chart;
    const rectHeight = chartHeight + plotHeight;
    const rectWidth = chartWidth * 0.1;
    const rectAttrs = { fill: '#253746', cursor: 'pointer', opacity: 0.25 };
    const chartContainer = chart.container;
    chartContainer.style.overflow = 'visible';
    this.setOverflow('visible', chart);
    const group = renderer.g('fullTimelineBtn').attr({ zIndex: 10 }).add();
    renderer
      .rect(0, -chartHeight, rectWidth, rectHeight)
      .attr(rectAttrs)
      .on('click', () => this.restoreClusteredView())
      .add(group);

    renderer
      .rect(chartWidth - rectWidth, -chartHeight, rectWidth, rectHeight)
      .attr(rectAttrs)
      .on('click', () => this.restoreClusteredView())
      .add(group);
    this.fullTimelineButtonGroup = group;
  }

  private updateExpandedRangeOverlay(
    chart: Highcharts.Chart,
    range: { min: number; max: number } | null
  ): void {
    this.clearExpandedRangeOverlay();
    if ((this.widget.payload as any).journeyTimelineRole !== 'nmot' || !range) {
      return;
    }
    const stripWidth = chart.plotWidth * 0.1;
    const shadeAttrs = { fill: '#253746', opacity: 0.15 };
    const group = chart.renderer.g('expandedRangeOverlay').attr({ zIndex: 3 }).add();
    chart.renderer
      .rect(chart.plotLeft, chart.plotTop, stripWidth, chart.plotHeight)
      .attr(shadeAttrs)
      .add(group);
    chart.renderer
      .rect(
        chart.plotLeft + chart.plotWidth - stripWidth,
        chart.plotTop,
        stripWidth,
        chart.plotHeight
      )
      .attr(shadeAttrs)
      .add(group);
    this.expandedRangeOverlayGroup = group;
  }

  private clearExpandedRangeOverlay(): void {
    this.expandedRangeOverlayGroup?.destroy();
    this.expandedRangeOverlayGroup = undefined;
  }

  private resetJourneyDrillState(): void {
    if (this.isJourneyDrillInProgress) {
      // Re-render caused by a drill: the drill level owns the state and must survive the refresh.
      return;
    }
    this.journeyDrillStack = [];
    this.currentJourneyDrillLevel = null;
    this.journeyDrillDateFilterValue = null;
    this.unsplittableJourneyBucketIds.clear();
    this.isClusterExpanded = false;
    this.fullTimelineButtonGroup?.destroy();
    this.fullTimelineButtonGroup = undefined;
    this.clearExpandedRangeOverlay();
  }

  private setOverflow(value: 'visible' | 'hidden', chart?: Highcharts.Chart): void {
    if (chart) {
      chart.container.style.overflow = value;
      const root = chart.container.querySelector('.highcharts-root') as HTMLElement;
      if (root) {
        root.style.overflow = value;
      }
    }
    ['.widget-container', '.content'].forEach((selector) => {
      const element = this.elementContent.nativeElement.closest(selector) as HTMLElement | null;
      if (element) {
        element.style.overflow = value;
      }
    });
  }

  public widgetContextMenuOpen(event: Highcharts.PointerEventObject): void {
    if (this.widgetComponent.isExpansionPanelShowing) {
      return;
    }
    setTimeout(() => {
      this.contextMenuTriggerNativeElement.nativeElement.setAttribute(
        'style',
        `left: ${event.chartX}px; top: ${event.chartY}px`
      );
      this.preparePointContext(event);
      this.pointSummaries = this.getSmartSummariesForPoint(this.pointName);

      if (this.isSmartSummaryEnabled) {
        this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu(this.pointIsTotalVolume);
        this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
      }
      this.contextMenuTriggerElement.openMenu();
    });
  }

  private preparePointContext(event: Highcharts.PointerEventObject): void {
    this.widgetContextMenu = { ...event['point'] };
    if (
      this.widget.payload.graphType === 'pie' ||
      this.widget.payload.graphType === 'gauge' ||
      this.isJourneyDrillChart
    ) {
      // For journey/NMOT charts, the shared "gap to neighboring point" heuristic used to build
      // the documents date-range filter is unreliable: journey series can have gaps between real
      // data points (e.g. days with no activity), which would otherwise widen the query far beyond
      // the single day the clicked point represents. Skip that heuristic here by setting category
      // to undefined (so the typeof number check in filter.service.ts fails) while preserving the
      // original value for pointName logic. The interval-bounded date range is added in getDocumentsQuery.
      const originalCategory = this.widgetContextMenu.category;
      this.widgetContextMenu.category = undefined;
      this.widgetContextMenu.originalCategory = originalCategory;
    }
    this.point = event['point'];
    this.pointComments = this.report.getCommentsForPoint(this.point, this.payload.comments) ?? [];
    this.activeComparisonBucketIdx = 0;
    if (this.comparison?.filters.length > 1) {
      this.activeComparisonBucketIdx = this.widgetContextMenu.series.index;
    }
    let isPointFilterable = !this.payload.customFieldSelected;
    if (!isPointFilterable && this.analyticItem) {
      const metadata = FieldService.getField(this.analyticItem);
      isPointFilterable =
        metadata.fieldType === 'keyword' || FieldService.isNumeric(this.analyticItem);
    }
    this.canAddFilterToWidgetGroup =
      !this.isFilterGroupParent &&
      isPointFilterable &&
      this.filterGroupFilterDrilledDownCheck &&
      !['sentimentVolume', 'gauge'].includes(this.widget.payload.type) &&
      !(
        this.widget.payload.type === 'sentimentPolarity' &&
        this.widget.payload.analyticItem === 'documents' &&
        this.point?.series?.name?.toLowerCase() === 'total volume'
      ) &&
      !this.dashboard.locked;
    this.logger.debug('point clicked', { point: event['point'] });
    this.setPointProps();
  }

  public setPointProps() {
    this.pointName = undefined;
    this.pointDisplayName = undefined;
    this.pointIsTotalVolume = undefined;
    this.pointHasDocuments = undefined;
    this.canCreateSmartSummary = undefined;
    if (!this.widgetContextMenu || !this.widgetContextMenu.options) {
      return '';
    }
    let inheritedValues = false;
    if (this.widget.payload.type === 'sentimentVolume') {
      this.pointName = this.widgetContextMenu.series.name;
      if ((this.report as any).getSelectedItem) {
        this.pointDisplayName = `${(this.report as any).getSelectedItem()} (${this.pointName})`;
      } else {
        this.pointDisplayName = this.pointName;
      }
      inheritedValues = true;
    } else if (
      this.report.reportName === 'taxonomyLevel' ||
      this.report.reportName === 'taxonomyTopFeaturesOverTime'
    ) {
      this.pointName = this.widgetContextMenu.options.pointId;
      this.pointDisplayName = this.widgetContextMenu.series.name;
      inheritedValues = true;
    } else if (
      this.widgetContextMenu.options.pointId &&
      this.widgetContextMenu.options.pointDisplay
    ) {
      this.pointName = this.widgetContextMenu.options.pointId;
      this.pointDisplayName = this.widgetContextMenu.options.pointDisplay;
      inheritedValues = true;
    } else if (this.widgetContextMenu.name) {
      this.pointName = this.widgetContextMenu.name;

      if (this.widget.payload.type === 'wordcloud') {
        this.pointName = this.widgetContextMenu.originalCategory ?? this.widgetContextMenu.category;
        this.pointDisplayName = this.widgetContextMenu.name;
        inheritedValues = true;
      }
    } else if (
      (this.widgetContextMenu.originalCategory ?? this.widgetContextMenu.category) ===
      this.widgetContextMenu.options.x
    ) {
      if (
        this.widgetContextMenu.series.chart.options.xAxis[0].type === 'datetime' &&
        (this.report.metadataType === 'date' || this.report.metadataType === 'double')
      ) {
        this.pointName = this.widgetContextMenu.options.key;
      } else {
        this.pointName = this.widgetContextMenu.series.name;
      }
    } else {
      if (this.report.reportName === 'taxonomySentimentPolarity') {
        const fullCategory =
          this.widgetContextMenu.originalCategory ?? this.widgetContextMenu.category;
        this.pointName = fullCategory.split(':')[0];
        this.pointDisplayName = fullCategory.split(':')[1];
        inheritedValues = true;
      } else {
        this.pointName = this.widgetContextMenu.originalCategory ?? this.widgetContextMenu.category;
      }
    }
    this.pointIsTotalVolume = !!this.widgetContextMenu.options.id?.includes('Total volume');

    if (
      this.widget.payload.type === 'breakdown' &&
      this.widget.payload.additionalBreakdownOptions
    ) {
      this.pointDisplayName = this.widgetContextMenu.options.id;
      inheritedValues = true;
    } else if (this.report.metadataType === 'date' || this.report.metadataType === 'double') {
      if (this.widget.payload.type === 'sentimentVolume') {
        this.pointDisplayName = `${this.report.formatMetadataValueFactory(
          this.report.options.selectedItems[0]
        )} (${this.pointName})`;
      } else if (this.report.reportName !== 'wordcloud') {
        this.pointDisplayName = this.report.formatMetadataValueFactory(this.pointName);
      }
    } else {
      if (!inheritedValues) {
        this.pointDisplayName = this.pointName;
      }
    }

    if (
      this.widget.payload.type === 'scatterPlot' &&
      this.widget.payload.analyticItem === 'taxonomy'
    ) {
      this.pointName = this.widgetContextMenu.series.userOptions['pointId'];
      this.pointDisplayName = this.widgetContextMenu.series.userOptions['name'];
    }

    if (this.pointName !== '&lt;blank&gt;') {
      this.pointName = Utils.unescapeHtml(this.pointName);
    }
    this.pointDisplayName = Utils.unescapeHtml(this.pointDisplayName || Constants.BLANK);
    this.pointHasDocuments = this.canShowDocuments;
    this.canCreateSmartSummary = this.isSmartSummaryEnabledForPoint;
    this.hasPointColorOverride = this.updateHasPointColorOverride();

    this.downloadSmartSummaryRequestToolTip = !this.canCreateSmartSummary
      ? translate('widgets.smart-summary.unavailable-for-points-with-less-than-documents', {
          count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
        })
      : '';
  }

  public get hideItemLabel(): string {
    if (this.widget.payload.limit < 0) {
      return translate('widgets.remove-from-custom-items');
    }
    return translate('widgets.hide-item-from-graph');
  }

  public async hideItem(explore = false) {
    // DASH-4765
    if (this.pointName === '&lt;blank&gt;') {
      delete this.widget.payload.showBlanks;
      return this.refresh();
    }

    if (this.gethasCustomSet()) {
      if (explore) {
        // DASH-2551 (revised): keep Custom widget as-is, just remove the explored item from selection
        this.widget.payload.selectedItems = this.widget.payload.selectedItems.filter(
          (analyticItem) => analyticItem !== this.pointName
        );
      } else if (this.widget.payload.selectedItems) {
        // hiding the item means removing it from the custom set according to DASH-1466
        const idx = this.widget.payload.selectedItems.indexOf(this.pointName);
        if (idx >= 0) {
          this.widget.payload.selectedItems.splice(idx, 1);
        }
      }
    }

    if (!this.widget.payload.excludedValues) {
      this.widget.payload.excludedValues = [];
    }

    this.widget.payload.excludedValues.push({
      name: this.pointName,
      type: this.analyticItem,
      displayName: this.pointDisplayName ? this.pointDisplayName : this.pointName,
      subType: this.widgetContextMenu.subType
        ? this.widgetContextMenu.subType
        : this.widget.payload.entityTypeFilter,
    });

    this.dashboardStore.markDirty();
    return this.refresh();
  }

  public openSmartSummary(pointName = this.pointName, query?) {
    super.openSmartSummary(pointName, query);
  }

  public removeSmartSummary() {
    super.removeSmartSummary();
  }

  onSmartSummaryClosed(result?) {
    super.onSmartSummaryClosed(result);

    if (result) {
      this.dashboardViewComponent.reload();
    }
  }

  onAddSmartSummaryComment($event) {
    const item = $event.target ?? this.point;

    this.widgetComponent.openCommentDialog(
      item,
      {
        title: $event.title,
        details: $event.details,
      },
      null,
      'smart-summary',
      $event.smartSummaryId
    );
  }

  async downloadSmartSummarySpotERequest() {
    this.downloadingSpoteRequest = true;
    this.downloadSmartSummaryRequestToolTip = translate(
      'widgets.smart-summary.currently-downloading-spote-request'
    );
    try {
      const queryString = this.getDocumentsQuery();
      // we keep this here if we somehow need this function to run but remove the assign since we are not using what it returns
      this.filterService.buildFilterStringClearPolarityFilters(
        this.widget.payload.filters,
        this.widget.payload.analyticItem
      );
      const analyticItemType = this.analyticItem ? this.analyticItem : 'metadata';
      const spotERequest = await this.interactiveSmartSummaryService.getSpotEReqestForSummary(
        this.widgetDataSource.collectionName,
        this.widgetDataSource.groupId,
        this.widgetDataSource.analysisId,
        {
          paragraphLength: 1,
          analyticItemName: this.pointName,
          analyticItemType,
        },
        this.timezoneService.timezone,
        queryString,
        this.widget.payload.projectId
      );
      const jsonString = JSON.stringify(spotERequest);
      const filename = `${analyticItemType}_${this.pointName}_spote_request.json`;
      const blob = new Blob([jsonString], { type: 'application/json' });
      saveAs(blob, filename);
    } finally {
      this.downloadingSpoteRequest = false;
      this.downloadSmartSummaryRequestToolTip = '';
    }
  }

  private resolveColorPointId(): string | undefined {
    const isBreakdownWithSecondary =
      this.widget.payload.type === 'breakdown' && this.widget.payload.additionalBreakdownOptions;
    return isBreakdownWithSecondary ? this.pointDisplayName : this.pointName;
  }

  private updateHasPointColorOverride(): boolean {
    const pointId = this.resolveColorPointId();
    const colors = this.widget.payload.customItemColors;

    if (!pointId || !colors?.length) {
      return false;
    }

    return colors.some((c) => JSON.parse(c)?.pointId === pointId);
  }

  public resetPointColor(): void {
    const pointId = this.resolveColorPointId();
    if (pointId) {
      this.resetItemColor.emit(pointId);
    }
  }

  public changeItemColor() {
    const chartColors = this.chartDisplayService.getChartColors(this.elementContent.nativeElement);
    this.pointNameForColorChange = this.resolveColorPointId();
    this.selectedItemColor = this.chartCustomColorService.getItemColor(
      this.widget,
      this.pointNameForColorChange,
      this.highchartsOptions,
      chartColors
    );
    setTimeout(() =>
      this.widgetChartColorPicker.nativeElement
        .querySelector('.kxi-color-square-wrapper div')
        .click()
    );
  }

  public onItemColorChange(rgbaColor: RGBAColor) {
    const colorString = `rgba(${rgbaColor.red},${rgbaColor.green},${rgbaColor.blue},${rgbaColor.alpha})`;
    const colorObject = { pointId: this.pointNameForColorChange, color: colorString };
    if (JSON.stringify(this.selectedItemColor) === JSON.stringify(rgbaColor)) {
      return;
    }

    this.syncItemColors.emit(colorObject);
  }

  public drilldownInto(
    item:
      | 'themes'
      | 'mentions'
      | 'taxonomy'
      | 'taxonomy_mentions'
      | 'topic_phrases'
      | 'entity_phrases'
      | 'taxonomy_phrases'
      | 'taxonomy_themes'
      | 'none',
    isDrillingDown: boolean,
    refreshOnDrillDown = false
  ): void {
    this.widget.payload.drilledDownLocation = this.pointName;
    this.widget.payload.isDrilledDown = isDrillingDown;
    this.widget.payload.drilledDownOn = item;
    if (!refreshOnDrillDown) {
      if (this.widget.payload.type === 'featureScoring') {
        if (item === 'none') {
          this.goBackClicked.emit();
        }
        super.refresh();
      } else {
        this.refresh();
      }
    }
  }

  public async drilldown() {
    if (!this.widget.payload.filters) {
      this.widget.payload.filters = FilterService.getDefaultFilterSet();
    }

    this.setAnalyticItemFilter({
      type: this.analyticItem,
      subType: this.payload.entityTypeFilter,
      name: this.pointName,
      displayName: this.pointDisplayName || this.pointName,
    });

    await this.hideItem(true);

    this.widgetComponent.updateTemplateBooleans();
  }

  protected setAnalyticItemFilter(node) {
    const itemType = node.type.split('_')[0];
    const itemSubType = itemType !== 'taxonomy' ? node.subType : undefined;

    // Section properties don't need a title suffix
    let analyticItem: string;
    let filterSet: FilterSet;

    if (node.type.includes('sectionProperties')) {
      analyticItem = node.type; // Use the full type like 'sectionProperties.sectionLabel'
      filterSet = (this.widget.payload.filters.sectionProperties ?? {
        and: [],
        or: [],
        not: [],
      }) as FilterSet;
    } else {
      const titleByType: Partial<Record<string, string>> = {
        themes: 'normalizedTitle',
        taxonomy: 'taxonomyFullPath',
      };
      const title = titleByType[itemType] ?? 'title';

      analyticItem = `${
        itemType === 'entities' && itemSubType ? 'ML-' : ''
      }analyses.${itemType}.${title}`;
      filterSet = this.widget.payload.filters[itemType] as FilterSet;
    }

    FilterService.setAnalyticItemFilter(
      filterSet,
      analyticItem,
      itemSubType,
      node.name,
      node.displayName,
      node.taxonomyPath,
      this.payload.selectedSection
    );
  }

  protected removeAnalyticItemFilter(node) {
    const itemType = node.type.split('_')[0];
    const itemSubType = node.subType;

    let analyticItem: string;
    let filterSet: FilterSet;

    if (node.type.includes('sectionProperties')) {
      analyticItem = node.type; // Use the full type like 'sectionProperties.sectionLabel'
      filterSet = (this.widget.payload.filters.sectionProperties ?? {
        and: [],
        or: [],
        not: [],
      }) as FilterSet;
    } else {
      const title = itemType === 'themes' ? 'normalizedTitle' : 'title';

      analyticItem = `${
        itemType === 'entities' && itemSubType ? 'ML-' : ''
      }analyses.${itemType}.${title}`;
      filterSet = this.widget.payload.filters[itemType] as FilterSet;
    }

    filterSet.and.forEach((item, index) => {
      if (item.analyticItem === analyticItem && item.value === node.name) {
        filterSet.and.splice(index, 1);
      }
    });
    this.widget.payload.filters[itemType] = filterSet;
  }

  public get widgetQueryOptions(): WidgetQueryOptions {
    let widgetQueryOptions: WidgetQueryOptions = this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup
    );

    if (this.widgetGroup?.isImpact) {
      const period = {
        first: this.widgetGroup?.parameters[this.activePeriodParam.first],
        last: this.widgetGroup?.parameters[this.activePeriodParam.last],
      };

      widgetQueryOptions = this.cpppService.assembleWidgetQueryOptions(
        this.widget,
        this.widgetDataSource,
        this.widgetGroup,
        period
      );
    }

    if (this.comparison) {
      widgetQueryOptions = this.filterService.getWidgetQueryOptions(
        this.widget,
        this.widgetDataSource,
        this.widgetGroup,
        this.comparison.filters[this.activeComparisonBucketIdx]
      );
    }

    if (this.widgetContextMenu) {
      widgetQueryOptions.widgetContextMenu = this.widgetContextMenu;
      widgetQueryOptions.subType = this.widgetContextMenu.subType || widgetQueryOptions.subType;
      if (
        this.widget.payload.type === 'sentimentPolarity' &&
        this.widgetContextMenu.series.name.toLowerCase() === 'total'
      ) {
        widgetQueryOptions.type = 'documentCount';
      }

      const seriesCustom = this.widgetContextMenu?.series?.options?.custom as
        | {
            scoring?: string;
            bucketIndex?: number;
            payloadProp?: string;
          }
        | undefined;
      if (
        this.widget.payload.type === 'breakdown' &&
        seriesCustom?.scoring === 'bucket' &&
        typeof seriesCustom.bucketIndex === 'number'
      ) {
        const payloadProp = seriesCustom.payloadProp;
        if (payloadProp === 'breakdownOptions' || payloadProp === 'additionalBreakdownOptions') {
          const bucketOptions: Partial<BreakdownOptions> | undefined =
            payloadProp === 'breakdownOptions'
              ? this.widget.payload.breakdownOptions
              : this.widget.payload.additionalBreakdownOptions;
          const bucketDefinition = Array.isArray(bucketOptions?.fieldBuckets)
            ? bucketOptions.fieldBuckets[seriesCustom.bucketIndex]
            : undefined;

          if (
            bucketOptions?.scoring === 'bucket' &&
            bucketOptions.numericMetadataFieldId &&
            bucketDefinition &&
            typeof bucketDefinition.minimumValue === 'number' &&
            typeof bucketDefinition.maximumValue === 'number'
          ) {
            widgetQueryOptions.bucketScoringQueryOptions = {
              fieldId: bucketOptions.numericMetadataFieldId,
              minimumValue: bucketDefinition.minimumValue,
              maximumValue: bucketDefinition.maximumValue,
              includeMinimum: bucketDefinition.includeMinimum === true,
              includeMaximum: bucketDefinition.includeMaximum === true,
            };
          }
        }
      }
    }

    widgetQueryOptions.pointName = this.pointName;
    widgetQueryOptions.analyticItem = this.analyticItem;
    widgetQueryOptions.parentPointName = this.parentPointName;
    widgetQueryOptions.analyticItems = this.analyticItems;

    return widgetQueryOptions;
  }

  protected get analyticItem(): string {
    return this.payload.analyticItem;
  }

  public async openSaveDocumentsToCollection(destinationType: 'NEW' | 'EXISTING') {
    if (this.widgetGroup?.isImpact) {
      this.activePeriodParam.first = this.widgetContextMenu.series.index
        ? 'currentStart'
        : 'previousStart';

      this.activePeriodParam.last = this.widgetContextMenu.series.index
        ? 'currentEnd'
        : 'previousEnd';
    }

    const queryOptions = this.widgetQueryOptions;
    if (this.pointName === '&lt;blank&gt;') {
      delete queryOptions.pointName;
      queryOptions.filters.metadata.and.push({
        analyticItem: 'metadata.' + queryOptions.analyticItem,
        value: '_notExists_',
      });
    }
    this.widgetComponent.openSaveDocumentsToCollection(
      destinationType,
      'REPORT',
      this.filterService.getQueryForDocuments(queryOptions)
    );
  }

  public get isPointTheBluePill() {
    if (this.widgetGroup) {
      return this.widgetGroup.parentWidget
        ? this.widget.id === this.widgetGroup.parentWidget
        : false;
    }
    return false;
  }

  private highlightBluePill() {
    if (this.isWidgetGroupParent) {
      let analyticItem = this.widget.payload.analyticItem
        ? JSON.parse(JSON.stringify(this.widget.payload.analyticItem))
        : undefined;
      if (this.widget.payload.type === 'taxonomyMap') {
        analyticItem = 'taxonomy';
      }
      if (this.widget.payload.customFieldSelected) {
        analyticItem = 'metadata';
      }
      if (this.widget.payload.standardFieldSelected) {
        analyticItem = 'core';
      }
      if (analyticItem && this.widgetGroup.parentFilters[analyticItem]?.and.length > 0) {
        const filter = this.widgetGroup.parentFilters[analyticItem].and[0];
        let filterValue = filter.value;
        if (filterValue.includes('numericrange')) {
          filterValue = this.filterService.numericRangeFilterToModel(filterValue).leftOperand.value;
        }

        if (
          this.widget.payload.type === 'sentimentPolarity' ||
          this.widget.payload.type === 'breakdown'
        ) {
          const series = this.highchartsOptions.series.find((s) => s.name === filter.seriesName);
          const data = series.data.find((d) => d.pointId === filterValue);
          if (data) {
            data.selected = true;
          }
        } else {
          //For datetime charts you can only select the value currently.
          //This will check for a match and select all the points of the line.
          if (
            this.highchartsOptions.xAxis?.type &&
            this.highchartsOptions.xAxis?.type === 'datetime'
          ) {
            for (let i = 0; i < this.highchartsOptions.series.length; i++) {
              if (
                this.highchartsOptions.series[i].name &&
                this.highchartsOptions.series[i].name === filter.value
              ) {
                const chartItems = this.highchartsOptions.series[i].data;
                for (let x = 0; x < chartItems.length; x++) {
                  chartItems[x].selected = true;
                }
              }
            }
          }
          const chartItems = this.highchartsOptions.series[0].data;
          for (let i = 0; i < chartItems.length; i++) {
            if (chartItems[i].pointId === filter.value || chartItems[i].name === filter.value) {
              if (analyticItem === 'taxonomy') {
                chartItems[i].color = '#66d9d1';
                chartItems[i].selected = true;
              } else {
                chartItems[i].selected = true;
              }
            } else {
              chartItems[i].selected = false;
            }
          }
        }
      }
    }
  }

  public addDocFilterGroupBluePill() {
    const result = {};
    result['filterGroup'] = this.dashboard.widgetGroups[this.widgetGroup.id];
    result['filterType'] = { title: translate('widgets.widget-filter.all-of-these'), id: 'and' };
    this.onAddFiltersToWidgetGroup(result, true);
  }

  public removeDocFilterGroupBluePill() {
    if (this.widgetGroup) {
      const group = this.dashboardViewComponent
        .widgetGroups()
        .find((g) => g.id === this.widgetGroup.id);
      group.parentFilters = FilterService.getDefaultFilterSet();
      const alertToDisplay = translate('widgets.parent-filter-removed', { group: group.title });
      this.dashboardViewComponent.onUpdateWidgetGroup(group, undefined, alertToDisplay, true);
    }
  }

  public async onAddFiltersToWidgetGroup(result, addAsParent = false) {
    if (result) {
      const analyticItem =
        this.payload.type === 'taxonomyMap' ? 'taxonomy' : this.payload.analyticItem;
      const filterAnalyticItem = this.mapAnalyticItemToFilterAnalyticItem(analyticItem);
      const queryOptions = this.widgetQueryOptions;
      const subLabel = queryOptions.subType;
      const buildFilter = (item) => {
        if (item !== 'documents') {
          let filterValue = this.pointName;
          let displayName = this.pointDisplayName;
          if (this.payload.type === 'taxonomyMap' && this.point['taxonomyPath']) {
            filterValue = `${this.point['taxonomyPath']}|${this.pointName}`;
          }

          if (this.widget.payload.customFieldSelected) {
            if (FieldService.isNumeric(this.payload.analyticItem)) {
              filterValue = this.filterService.numericRangeModelToFilter({
                leftOperand: {
                  operator: '=',
                  value: this.pointName,
                },
              });
              displayName = this.filterService.translateKey(this.payload.analyticItem, filterValue);
            }
          }
          return {
            analyticItem: filterAnalyticItem,
            value: filterValue,
            displayName,
            subLabel,
            seriesName: this.point['series'].name,
          };
        }

        if (this.widget.payload.type === 'documentCount') {
          if (ChartOptionUtils.isOverTimeAggregator(this.widget.payload.aggregator)) {
            const dateRange = this.filterService.generateDateRangeFilter(
              this.point.category,
              this.point.index,
              this.point.series.data,
              this.widget.payload.dateInterval
            );
            return {
              analyticItem: `createdAt`,
              value: `daterange/${dateRange.fromDate.format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT)}/${dateRange.toDate.format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT)}`,
              key: 'daterange',
              seriesName: this.point['series'].name.toLowerCase(),
            };
          }
          return {
            analyticItem: `analyses.sentimentPolarity${this.dashboard.payload.sentimentScale}`,
            value: this.point['series'].name.toLowerCase(),
            displayName: this.point['series'].name.toLowerCase(),
            subLabel,
            seriesName: this.point['series'].name.toLowerCase(),
          };
        }

        return {
          analyticItem: `analyses.sentimentPolarity${this.dashboard.payload.sentimentScale}`,
          value: this.point['series'].name.toLowerCase(),
          displayName: this.point['series'].name.toLowerCase(),
          subLabel,
          seriesName: this.point['series'].name.toLowerCase(),
        };
      };

      result['filter'] = buildFilter(analyticItem);
      const group = this.dashboardViewComponent
        .widgetGroups()
        .filter((g) => g.id === result['filterGroup'].id)[0];

      let analyticItemForFilters = queryOptions.analyticItem;
      if (queryOptions.customFieldSelected) {
        analyticItemForFilters = 'metadata';
      }
      if (queryOptions.standardFieldSelected) {
        analyticItemForFilters = 'core';
      }
      if (queryOptions.analyticItem === 'createdAt') {
        analyticItemForFilters = 'dates';
      }
      if (queryOptions.analyticItem === 'documents') {
        analyticItemForFilters = 'analyses';
        if (
          this.widget.payload.type === 'documentCount' &&
          ChartOptionUtils.isOverTimeAggregator(this.widget.payload.aggregator)
        ) {
          analyticItemForFilters = 'dates';
        }
      }
      let alertToDisplay;
      const parsedFilter = JSON.parse(JSON.stringify(result['filter']));
      if (addAsParent) {
        parsedFilter['parentFilter'] = true;
        group.parentFilters = FilterService.getDefaultFilterSet();
        group.parentFilters[analyticItemForFilters][result['filterType'].id].push(parsedFilter);
        alertToDisplay = translate('widgets.parent-filter-set', { group: group.title });
      } else {
        group.filters[analyticItemForFilters][result['filterType'].id].push(parsedFilter);
      }
      group.lastUsedDataSource = this.widgetDataSourceId;
      group.lastUsedDataSourceDisplayName = this.widgetDataSource.displayName;

      this.dashboardViewComponent.onUpdateWidgetGroup(group, undefined, alertToDisplay, true);
    }
    this.showAddToWidgetGroupModal = false;
  }

  protected getDocumentsQuery(initialQuery?: string) {
    let query = initialQuery;
    if (!initialQuery) {
      const queryOptions = this.widgetQueryOptions;
      if (this.pointName === '&lt;blank&gt;') {
        delete queryOptions.pointName;
        queryOptions.filters.metadata.and.push({
          analyticItem: 'metadata.' + queryOptions.analyticItem,
          value: '_notExists_',
        });
      }
      if (this.isJourneyTouchpointsChart) {
        queryOptions.dateInterval = this.journeyDrillInterval;
      }
      query = this.filterService.getQueryForDocuments(queryOptions);
      if (this.isJourneyDrillChart && typeof this.widgetContextMenu?.x === 'number') {
        // preparePointContext strips widgetContextMenu.category for journey/NMOT charts so the
        // shared neighbor-gap date-range heuristic is skipped. Bound the query to the exact
        // interval bucket (day/week/month/etc., matching the chart's configured Time Interval)
        // containing the clicked point instead, avoiding overly wide windows when neighboring
        // points are sparse.
        const pointRange = this.getJourneyPointIntervalRange(
          this.widgetContextMenu.x,
          this.journeyDrillInterval
        );
        query += ` AND ${this.filterService.generateDateRangeQueryPart('createdAt', pointRange)}`;
      }
    }
    return query;
  }

  private getJourneyPointIntervalRange(
    timestamp: number,
    dateInterval?: string
  ): { fromDate: Moment; toDate: Moment } {
    const unit: moment.unitOfTime.StartOf = (ChartDateInterval as readonly string[]).includes(
      dateInterval
    )
      ? (dateInterval as moment.unitOfTime.StartOf)
      : 'day';
    const fromDate = moment.tz(timestamp, this.timezoneService.timezone).startOf(unit);
    const toDate = moment.tz(timestamp, this.timezoneService.timezone).endOf(unit);
    return { fromDate, toDate };
  }

  public async showDocumentsForItem(initialQuery?: string) {
    let analyticItem = this.payload.analyticItem;

    if (
      this.widget.payload.drilledDownOn === 'taxonomy_phrases' ||
      this.widget.payload.drilledDownOn === 'topic_phrases' ||
      this.widget.payload.drilledDownOn === 'entity_phrases'
    ) {
      analyticItem = 'phrases';
    } else if (this.widget.payload.drilledDownOn === 'themes') {
      analyticItem = this.widget.payload.drilledDownOn;
    } else if (this.widget.payload.drilledDownOn === 'taxonomy_themes') {
      analyticItem = 'themes';
    }

    const query = this.getDocumentsQuery(initialQuery);

    let page;

    try {
      const searchOptions: SearchOptions = {
        timezone: this.timezoneService.timezone,
      };

      if (this.isJourneyDrillChart) {
        page = await this.documentService.searchForJourneyGroupDocuments(
          this.widgetDataSource,
          this.dashboard.project_id,
          query,
          {
            ...searchOptions,
            detailed: this.widgetDataSource.groupAnalyses?.join() || '*',
            journeySubject: {
              subject: this.payload.subject,
              subjectValue: this.payload.subjectValue,
            },
          }
        );
      } else {
        page = await this.documentService.searchForDocuments(
          this.widgetDataSource,
          this.dashboard.project_id,
          this.widgetDataSource.collectionName,
          query,
          20,
          searchOptions
        );
      }
    } catch (error) {
      this.alertService.showErrorToast(translate('widgets.error-receiving-documents'), error);
      return;
    }

    const documentId = page.count ? page.items[0].id : null;
    if (documentId === null) {
      this.alertService.showErrorToast(translate('widgets.error-receiving-documents'));
      return;
    }

    this.documentViewModalOptions = {
      collectionName: page.items[0].collectionName,
      page,
      pageIndex: 0,
      documentIndex: 0,
      limit: 20,
      documentId,
      projectId: this.dashboard.project_id,
      entity: this.pointName,
      entityDisplayName: this.pointDisplayName,
      analyticItem,
      sectionPropertySelected: this.payload.sectionPropertySelected,
      sentimentScale: this.payload.sentimentScale,
      sentimentDisplay: this.dashboard.payload.sentimentDisplay,
      dashboardId: this.dashboard.id,
      isDashboardLocked: this.dashboard.locked,
      sentimentRanges: {
        feature: this.project.polarities.feature[this.payload.sentimentScale],
        document: this.project.polarities.document[this.payload.sentimentScale],
      },
      widgetDataSource: this.widgetDataSource,
    };
    this.showDocumentViewModal = true;
    this.cdr.markForCheck();
  }

  onDocumentViewModalClosed(result?) {
    this.showDocumentViewModal = false;
    this.cdr.markForCheck();
    if (result) {
      this.dashboardViewComponent.reload();
    }
  }

  public get isDataSourceNotFound() {
    return this.dataSourceNotFound;
  }

  get isChartHidden(): boolean {
    return (
      this.isLoading ||
      this.isShowAsDataTable ||
      this.isCustomSortMode ||
      this.noData ||
      this.isDataSourceNotFound ||
      this.hasInvalidComparison ||
      this.noPermission ||
      this.noDocuments
    );
  }

  //For testing override
  public setWidgetContextMenu(widgetContextMenu: any) {
    this.widgetContextMenu = widgetContextMenu;
    this.activeComparisonBucketIdx = 0;
    if (this.comparison?.filters.length > 1) {
      this.activeComparisonBucketIdx = this.widgetContextMenu.series.index;
    }
    this.setPointProps();
  }

  public get customSetLabel() {
    return this.payload.selectedItems ? this.payload.selectedItems.join(', ') : '';
  }

  private getFilterDate(minMax: 0 | 1, filters: Filter[] | undefined | null): Date | undefined {
    if (!filters) {
      return undefined;
    }
    let filterDate: Date | undefined;
    for (const filter of filters) {
      if (filter.analyticItem === 'createdAt') {
        const filterDates: [string, string] = this.filterService.computeFixedValueForDateFilter(
          filter.key,
          filter.value
        );
        if (filterDates[minMax] !== '*') {
          const currentFilterDate = new Date(filterDates[minMax]);

          filterDate ??= currentFilterDate;

          if (minMax === 0) {
            filterDate = filterDate < currentFilterDate ? currentFilterDate : filterDate;
          } else {
            filterDate = filterDate > currentFilterDate ? currentFilterDate : filterDate;
          }
        }
      }
    }
    return filterDate;
  }

  private calculateDateInterval(): WidgetChartOptions['dateInterval'] {
    if (!this.widgetDataSource) {
      return undefined;
    }
    //If this is an older group that doesn't have the date data, then default to day.
    if (this.widgetDataSource.type === 'group' && this.widgetDataSource.sourceVersion < 3) {
      return this.payload.dateInterval ? this.payload.dateInterval : 'day';
    }

    //If no date data in the datasource we don't want the interval.
    if (
      !this.widgetDataSource.hasDateData ||
      (!this.widgetDataSource.minDocAt && !this.widgetDataSource.maxDocAt)
    ) {
      return undefined;
    }

    let mindateSeconds = new Date(this.widgetDataSource.minDocAt).getTime() / 1000;
    let maxdateSeconds = new Date(this.widgetDataSource.maxDocAt).getTime() / 1000;

    let comparisonFilterMin: Date;
    let comparisonFilterMax: Date;
    if (this.comparison) {
      this.comparison.filters.forEach((fs) => {
        const min = this.getFilterDate(0, fs.and);
        const max = this.getFilterDate(1, fs.and);

        comparisonFilterMin ??= min;
        comparisonFilterMax ??= max;

        comparisonFilterMin = comparisonFilterMin < min ? min : comparisonFilterMin;
        comparisonFilterMax = comparisonFilterMax > max ? max : comparisonFilterMax;
      });
    }

    //FilterDates
    const filterMin =
      this.getFilterDate(0, this.payload.filters?.dates?.and) ??
      this.getFilterDate(0, this.widgetGroup?.andFilters) ??
      comparisonFilterMin;

    const filterMinDateSeconds = filterMin ? filterMin.getTime() / 1000 : undefined;

    //For min we want to clamp the range only if the filter has a range inside of the document range
    //therefore greater than the document min.
    if (filterMinDateSeconds && filterMinDateSeconds > mindateSeconds) {
      mindateSeconds = filterMinDateSeconds;
    }

    const filterMax =
      this.getFilterDate(1, this.payload.filters?.dates?.and) ??
      this.getFilterDate(1, this.widgetGroup?.andFilters) ??
      comparisonFilterMax;

    const filterMaxDateSeconds = filterMax ? filterMax.getTime() / 1000 : undefined;
    //For max we want to clamp the range only if the filter has a range inside of the document range
    //therefore less than the document max.
    if (filterMaxDateSeconds && filterMaxDateSeconds < maxdateSeconds) {
      maxdateSeconds = filterMaxDateSeconds;
    }

    const timeDifferenceInSeconds = Math.abs(maxdateSeconds - mindateSeconds);

    //Minutes
    if (timeDifferenceInSeconds > 0 && timeDifferenceInSeconds <= 6000) {
      return 'minute';
    }

    const timeDifferenceInHours = timeDifferenceInSeconds / 60 / 60;
    //Hours
    if (timeDifferenceInHours >= 0 && timeDifferenceInHours <= 100) {
      return 'hour';
    }

    const timeDifferenceInDays = timeDifferenceInHours / 24;
    //Days
    if (timeDifferenceInDays >= 0 && timeDifferenceInDays <= 100) {
      return 'day';
    }

    //Weeks
    //Cutting off at 50 rather than 100 because there's less of a jump between weeks and months
    if (timeDifferenceInDays / 7 >= 0 && timeDifferenceInDays / 7 <= 100) {
      return 'week';
    }

    //Months (101 to take into account not all months have 30 days)
    if (timeDifferenceInDays / 30 >= 0 && timeDifferenceInDays / 30 <= 101) {
      return 'month';
    }

    //Quarters (101 to take into account not all months have 30 days)
    if (timeDifferenceInDays / 90 >= 0 && timeDifferenceInDays / 90 <= 101) {
      return 'quarter';
    }

    //Years
    return 'year';
  }

  public showMentionsToolTip() {
    if (this.hasMentions()) {
      return undefined;
    }
    if (this.payload.type === 'taxonomyMap') {
      return translate('widgets.no-item-mentions-in-dataset', {
        item: translate('widgets.topics'),
      });
    }
    return translate('widgets.no-item-mentions-in-dataset', {
      item:
        this.payload.customFieldSelected || this.payload.standardFieldSelected
          ? FieldService.getFieldLongName(this.payload.analyticItem)
          : translate('widgets.' + this.payload.analyticItem),
    });
  }

  public showTopicPhrasesTooltip() {
    if (!this.hasTopicPhrases()) {
      return translate('widgets.no-phrases-for-topics-in-dataset');
    }

    if (this.isTopicOnlyEie()) {
      return translate('widgets.not-available-for-this-item');
    }
  }

  public showTaxonomyThemesTooltip() {
    if (!this.hasTopicThemes()) {
      return translate('widgets.no-themes-for-topics-in-dataset');
    }
  }

  public showTopicThemesTooltip() {
    if (!this.hasTopicThemes()) {
      return translate('widgets.no-themes-for-topics-in-dataset');
    }

    if (this.isTopicOnlyEie()) {
      return translate('widgets.not-available-for-this-item');
    }
  }

  public showTopicPhraseOption() {
    return this.widget.payload.analyticItem === 'topics';
  }

  public showTopicPhrasePolarityOption() {
    return this.showTopicPhraseOption() && this.widget.payload.type === 'sentimentPolarity';
  }

  public showEntityPhraseOption() {
    return this.widget.payload.analyticItem === 'entities';
  }

  public showEntityPhrasesTooltip() {
    if (!this.hasEntityPhrases()) {
      return translate('widgets.no-phrases-for-entities-in-dataset');
    }
  }

  public showDrilldownTooltip() {
    if (this.hasOccurences()) {
      return undefined;
    }

    let type = '';
    if (this.analyticItem === 'documents') {
      type = translate('widgets.documents');
    } else if (this.payload.standardFieldSelected) {
      type = translate('widgets.standard-fields');
    } else if (this.payload.customFieldSelected) {
      type = translate('widgets.custom-fields');
    }

    return translate('widgets.explore-not-available', { item: type });
  }

  public showHideItemToolTip() {
    if (this.enableHideItems()) {
      return undefined;
    }

    if (this.widget.payload.analyticItem === 'documents') {
      return translate('widgets.hide-not-available-documents');
    }

    if (this.widget.payload.selectedItems.length === 0) {
      return translate('widgets.hide-not-available-last-item');
    }
  }

  public hasMentions() {
    if (!this.widgetDataSource) {
      return false;
    }

    if (this.widgetDataSource.type === 'analysis' && this.widgetDataSource.sourceVersion < 3) {
      return true;
    }

    if (this.widgetDataSource.type === 'group' && this.widgetDataSource.sourceVersion < 4) {
      return true;
    }

    if (this.payload.type === 'taxonomyMap') {
      return this.widgetDataSource.hasTopicMentions;
    }
    switch (this.widget.payload.analyticItem) {
      case 'topics':
        return this.widgetDataSource.hasTopicMentions;
      case 'themes':
        return this.widgetDataSource.hasThemeMentions;
      case 'entities':
        return this.widgetDataSource.hasEntityMentions;
      default:
        return false;
    }
  }

  public hasTopicPhrases() {
    if (this.isSentimentBased && this.pointIsTotalVolume && this.analyticItem === 'topics') {
      return false;
    }
    return this.widgetDataSource ? this.widgetDataSource.hasTopicPhrases : false;
  }

  public hasTopicThemes() {
    return this.widgetDataSource ? this.widgetDataSource.hasTopicThemes : false;
  }

  public hasEntityPhrases() {
    return this.widgetDataSource ? this.widgetDataSource.hasEntityPhrases : false;
  }

  isTopicOnlyEie(): boolean {
    if (!this.pointName) {
      return false;
    }

    if (this.report.reportName !== 'documentTopFeatures') {
      return false;
    }

    return !!this.reportExtra?.[this.pointName]?.every((t) => t === 'clause_models');
  }

  public isDateIntervalEnabled(dateInterval) {
    return (
      ChartDateInterval.indexOf(this.minDateInterval) <= ChartDateInterval.indexOf(dateInterval) &&
      ChartDateInterval.indexOf(this.maxDateInterval) >= ChartDateInterval.indexOf(dateInterval)
    );
  }

  onCommentAdded(comment: Comment): void {
    this.chartCommentsService.addComment(
      this.elementContent.nativeElement,
      comment,
      this.widget.payload.commentsHidden,
      this.annotationClickHandlerFn(),
      this.annotationDragHandlerFn()
    );
  }

  onCommentDeleted(comment: Comment): void {
    const isCommentVisible = this.chartCommentsService.removeComment(
      this.elementContent.nativeElement,
      comment
    );
    if (!isCommentVisible) {
      return;
    }

    const nextComment = this.widget.payload.comments.find(
      (c) => c.target?.pointId === comment.target?.pointId && c.id !== comment.id
    );
    if (nextComment) {
      this.chartCommentsService.addComment(
        this.elementContent.nativeElement,
        nextComment,
        this.widget.payload.commentsHidden,
        this.annotationClickHandlerFn(),
        this.annotationDragHandlerFn()
      );
    }
  }

  onCommentEdited(comment: Comment): void {
    this.chartCommentsService.editComment(
      this.elementContent.nativeElement,
      comment,
      this.widget.payload.commentsHidden,
      this.annotationClickHandlerFn(),
      this.annotationDragHandlerFn()
    );
  }

  isCommentVisible(comment: Comment): boolean {
    return this.chartCommentsService.isCommentVisible(this.elementContent.nativeElement, comment);
  }

  getVisibleComments() {
    if (this.widget.payload.commentsHidden) {
      return [];
    }
    return this.chartCommentsService.getVisibleComments(
      this.elementContent.nativeElement,
      this.payload.comments
    );
  }

  getCommentChangeForComment(comment) {
    if (!comment) {
      return;
    }

    return this.chartCommentsService.getCommentChangeForComment(
      this.elementContent.nativeElement,
      comment
    );
  }

  itemToCommentPoint(item: any): CommentPoint {
    return this.report.chartItemToCommentPoint(item);
  }

  itemToCommentAnalyticItem() {
    return [
      {
        name: this.pointName,
        displayName: this.pointDisplayName,
        type: this.widget.payload.analyticItem,
        subType: this.widget.payload.entityTypeFilter,
      },
    ];
  }

  public async turnPolarityPointIntoPhraseWordCloud() {
    const polarityFilter: Filter = {
      analyticItem: 'analyses.topics.sentimentPolarity' + this.widget.payload.sentimentScale,
      hidden: true,
      value: this.widgetContextMenu.series.name.toLowerCase(),
    };
    const widgetCopy = JSON.parse(
      JSON.stringify(this.widget, (_, v) => (v === undefined ? undefined : v))
    ); // preserve undefined values
    widgetCopy.id = UUIDv4();
    widgetCopy.title = translate('widgets.topic-phrases-of', { item: this.pointDisplayName });
    widgetCopy.payload.filters.topics.or = [polarityFilter];
    widgetCopy.payload.type = 'wordcloud';
    widgetCopy.payload.taxonomyLevel = this.pointName;
    widgetCopy.payload.drilledDownLocation = this.pointName;
    widgetCopy.payload.isDrilledDown = true;
    widgetCopy.payload.drilledDownOn = 'topic_phrases';
    widgetCopy.payload.aggregator = undefined;
    widgetCopy.payload.orderBySentiment = false;
    widgetCopy.payload.topicPhraseCloud = true;
    this.addWidgetToDashboardToRight(widgetCopy);
  }

  protected addWidgetToDashboardToRight(newWidget: Widget) {
    const alignedWidgets = [];
    for (const widget of this.dashboard.widgets) {
      alignedWidgets.push(widget);
      if (widget.id === this.widget.id) {
        alignedWidgets.push(newWidget);
      }
    }
    this.dashboardViewComponent.forceRedraw(() => {
      this.dashboard.widgets = alignedWidgets;
      this.dashboardViewComponent.currentPageWidgets = this.dashboard.widgets.filter(
        (w) => w.payload.page === this.dashboardViewComponent.currentPageId
      );
    });
  }

  public showTopicPhraseWordCloudToolTip() {
    if (this.hasTopicPhrases()) {
      return translate('widgets.create-wordcloud-topic-phrases');
    }
    if (this.isSentimentBased && this.analyticItem === 'topics' && this.pointIsTotalVolume) {
      return translate('widgets.topic-phrase-wordcloud-unavailable-for-sentiment');
    }

    return translate('widgets.no-phrases-for-topics-in-dataset');
  }

  public get isDrilledDown() {
    return (
      this.widget.payload.isDrilledDown &&
      !!this.widget.payload.drilledDownOn &&
      this.widget.payload.drilledDownOn !== 'none'
    );
  }

  public get topicPhraseCloud() {
    return this.widget.payload.topicPhraseCloud;
  }

  public get drilledDownOn() {
    return this.widget.payload.drilledDownOn;
  }

  public get filterGroupFilterDrilledDownCheck() {
    return !this.isDrilledDown || ['taxonomy', 'none'].includes(this.drilledDownOn);
  }

  public isDataSourceChangeDisabled(): boolean {
    return !!this.widget.payload.topicPhraseCloud;
  }

  public isAnalyticItemChangeDisabled(): boolean {
    return !!this.widget.payload.topicPhraseCloud;
  }

  public canShowDocumentList() {
    if (this.hasInvalidComparison) {
      return false;
    }

    return super.canShowDocumentList();
  }

  public canShowDataTable() {
    if (this.hasInvalidComparison) {
      return false;
    }

    return super.canShowDataTable();
  }

  public get isShowAsDataTable(): boolean {
    return this.widget.properties.as_data_table;
  }

  goBackButtonClicked() {
    this.goBackClicked.emit();
  }

  get canSort(): boolean {
    if (this.isCustomSortMode) {
      return false;
    }

    if (!this.report?.canSort()) {
      return false;
    }

    if (
      (this.comparison?.filters.length > 1 && this.widget.payload.type !== 'donut') ||
      this.widget.payload.type === 'gauge'
    ) {
      return false;
    }

    return true;
  }

  isSortDisabled() {
    return (
      this.widgetComponent.isExpansionPanelShowing ||
      this.widget.payload.selectedItems?.length === 1
    );
  }

  get currentSort() {
    return ChartSortHelper.getCurrentSort(this.widget);
  }

  get verticalAxisField() {
    return this.widget.payload.graphType === 'bar' ? 'label' : 'value';
  }

  get horizontalAxisField() {
    return this.widget.payload.graphType === 'bar' ? 'value' : 'label';
  }

  get verticalAxisSortTooltip() {
    if (this.widget.payload.graphType === 'bar') {
      return this.sortByLabelTooltip;
    }
    return this.sortByValueTooltip;
  }

  get horizontalAxisSortTooltip() {
    if (this.widget.payload.graphType === 'bar') {
      return this.sortByValueTooltip;
    }
    return this.sortByLabelTooltip;
  }

  get customSortTooltip() {
    if (this.isSortDisabled()) {
      if (this.widgetComponent.isExpansionPanelShowing) {
        return translate('widgets.can-not-sort-expansion-panel');
      }
      return translate('widgets.can-not-sort-single-item');
    }

    return translate('widgets.custom-sort');
  }

  private get sortByValueTooltip() {
    if (this.isSortDisabled()) {
      if (this.widgetComponent.isExpansionPanelShowing) {
        return translate('widgets.can-not-sort-expansion-panel');
      }
      return translate('widgets.can-not-sort-single-item');
    }

    if (!this.sortButtonProps.value.tooltip) {
      let sentimentPart = '';

      if (this.currentSort.sentiment) {
        sentimentPart =
          this.currentSort.sentiment.charAt(0).toUpperCase() + this.currentSort.sentiment.slice(1);
      }

      this.sortButtonProps.value.tooltip = translate('widgets.sort-sentiment-descending', {
        sentiment: sentimentPart,
      });
      if (this.currentSort.field === 'value') {
        this.sortButtonProps.value.tooltip = translate('widgets.sorted-by-sentiment-descending', {
          sentiment: sentimentPart,
        });
        if (this.currentSort.order === 'asc') {
          this.sortButtonProps.value.tooltip = translate('widgets.sorted-by-sentiment-ascending', {
            sentiment: sentimentPart,
          });
        }
      }
    }

    return this.sortButtonProps.value.tooltip;
  }

  private get sortByLabelTooltip() {
    if (this.isSortDisabled()) {
      if (this.widgetComponent.isExpansionPanelShowing) {
        return translate('widgets.can-not-sort-expansion-panel');
      }
      return translate('widgets.can-not-sort-single-item');
    }

    if (!this.sortButtonProps.label.tooltip) {
      this.sortButtonProps.label.tooltip = translate('widgets.sort-alpha-descending');

      const isCustomNumeric = this.isAnalyticItemCustomNumeric();

      const { field, order } = this.currentSort;
      if (field === 'label') {
        const isAscending = order === 'asc';
        let tooltipKey = isAscending
          ? 'widgets.sorted-by-alpha-ascending'
          : 'widgets.sorted-by-alpha-descending';
        if (isCustomNumeric) {
          tooltipKey = isAscending ? 'widgets.sorted-by-ascending' : 'widgets.sorted-by-descending';
        }
        this.sortButtonProps.label.tooltip = translate(tooltipKey);
      } else if (isCustomNumeric) {
        this.sortButtonProps.label.tooltip = translate('widgets.sort-descending');
      }
    }

    return this.sortButtonProps.label.tooltip;
  }

  getSortIcon(axis: ChartSort['field']) {
    if (!this.sortButtonProps[axis].icon) {
      this.sortButtonProps[axis].icon = axis === 'label' ? 'width' : 'sort';

      if (this.currentSort.field === axis) {
        this.sortButtonProps[axis].icon = 'sort_order_desc';
        if (this.currentSort.order === 'asc') {
          this.sortButtonProps[axis].icon = 'sort_order_asc';
        }
      }
    }

    return this.sortButtonProps[axis].icon;
  }

  sortChartItems(axis: ChartSort['field']) {
    const currentSort = { ...this.currentSort };

    this.sortButtonProps = {
      label: {
        icon: undefined,
        tooltip: undefined,
      },
      value: {
        icon: undefined,
        tooltip: undefined,
      },
    };

    if (currentSort.field === axis) {
      currentSort.order = currentSort.order === 'asc' ? 'desc' : 'asc';

      if (axis === 'value' && currentSort.sentiment && currentSort.order === 'desc') {
        switch (currentSort.sentiment) {
          case 'positive':
            currentSort.sentiment = 'negative';
            break;
          case 'negative':
            currentSort.sentiment = 'total';
            break;
          case 'total':
            currentSort.sentiment = 'positive';
            break;
          case 'Top':
            currentSort.sentiment = 'Bottom';
            break;
          case 'Bottom':
            currentSort.sentiment = 'Top';
            break;
        }
      }
    } else {
      currentSort.field = axis;
      currentSort.order = DEFAULT_SORT.order;

      if (currentSort.sentiment) {
        switch (this.widget.payload.type) {
          case 'sentimentPolarity':
            currentSort.sentiment = DEFAULT_SORT_SENTIMENT_POLARITY.sentiment;
            break;
          case 'breakdown':
            if (this.widget.payload.breakdownOptions.scoring === 'box') {
              currentSort.sentiment = DEFAULT_SORT_BREAKDOWN_BOX.sentiment;
            }
            break;
          default:
            break;
        }
      }
    }

    this.widget.payload.sort = currentSort;
    delete this.widget.payload.sort.items;

    this.chartDisplayService.sortChart(
      this.widget,
      this.elementContent.nativeElement,
      this.isAnalyticItemOfCustomType('date'),
      this.isAnalyticItemCustomNumeric()
    );
    this.dashboardStore.markDirty();
  }

  enableCustomSort() {
    this.customSortOrder = this.chartDisplayService.getChartItems(
      this.elementContent.nativeElement
    );

    if (this.widget.payload.analyticItem === 'taxonomy') {
      this.customSortOrder.forEach((e) => {
        if (e.label.includes(':')) {
          e.label = e.label.split(':')[1];
        }
      });
    }

    this.isCustomSortModeChange.emit(true);
  }

  onItemsReordered($event) {
    moveItemInArray(this.customSortOrder, $event.previousIndex, $event.currentIndex);
  }

  finishCustomSort(confirm) {
    if (confirm) {
      this.sortButtonProps = {
        label: {
          icon: undefined,
          tooltip: undefined,
        },
        value: {
          icon: undefined,
          tooltip: undefined,
        },
      };

      this.widget.payload.sort = {
        field: 'custom',
        order: 'desc',
        sentiment: ChartSortHelper.getDefaultSort(this.widget).sentiment,
        items: this.customSortOrder.map((o) => o.name),
      };

      const seriesOrder = this.chartDisplayService.sortChart(
        this.widget,
        this.elementContent.nativeElement,
        this.isAnalyticItemOfCustomType('date'),
        this.isAnalyticItemCustomNumeric()
      );

      this.widget.payload.sort.items = seriesOrder;
      this.dashboardStore.markDirty();
    }
    this.isCustomSortModeChange.emit(false);
  }

  getScatterPlotAxisName(axis) {
    return ScatterPlotUtils.getAxisName(axis);
  }

  getScatterPlotColumnHeader(axis, interval) {
    return ScatterPlotUtils.getDatatableColumnHeader(axis, this.widgetGroup?.isImpact && interval);
  }

  transformLabel(value, type): string {
    if (value === Constants.BLANK) {
      return value;
    } else if (value === '&lt;blank&gt;') {
      return Utils.unescapeHtml(value);
    } else {
      switch (type) {
        case 'date':
          return this.lexDatePipe.transform(value);
        case 'double':
          return Utils.round(value, 2).toString();
        case 'long':
          return Utils.round(value, 0).toString();
        default:
          return value;
      }
    }
  }

  public override canApplyComparison(comparison?): boolean {
    if (
      this.widget.payload.type === 'numericMetadata' &&
      this.widget.payload.numericMetadataFields?.length === 1
    ) {
      return true;
    }

    if (
      (this.widget.payload.type === 'donut' || this.widget.payload.type === 'gauge') &&
      comparison?.filters.length === 2
    ) {
      return true;
    }

    return super.canApplyComparison(comparison);
  }

  public override getComparisonErrorMessage(comparison?: Comparison): string | undefined {
    if (this.widget.payload.type === 'numericMetadata' && !this.canApplyComparison(comparison)) {
      return 'widgets.comparison-nmot-multiple-fields';
    }
  }

  annotationClickHandlerFn() {
    const self = this;
    return function () {
      self.point = this.labels[0].points[0];
      self.pointComments = self.report.getCommentsForPoint(self.point, self.payload.comments);
      self.commentsHandlerService.showExpansionPanel({
        widgetId: self.widget.id,
        widgetContainer: self.widgetComponent.widgetContainer,
        panelData: self.point,
        panelType: 'COMMENTS',
      });
    };
  }

  hideOrShowAllComments() {
    this.chartCommentsService.hideOrShowComments(
      this.elementContent.nativeElement,
      this.widget.payload.commentsHidden
    );
  }

  setAnnotationSelected(commentTarget: CommentTarget) {
    this.chartCommentsService.setAnnotationSelected(
      this.elementContent.nativeElement,
      commentTarget?.pointId
    );
  }

  override getAiInsightDataPointCount(handler: MatchmakerHandler): number {
    return this.chartPointSelectionService.getDataPointCount(
      this.elementContent.nativeElement,
      this.aiInsightValidatorService.getPointSelectionRule(handler)
    );
  }

  override getAiInsightEligiblePointCount(): number {
    const chart = this.chartDisplayService.getChart(this.elementContent.nativeElement);
    if (!chart) {
      return 0;
    }

    const eligiblePoints = chart.series
      .filter((series) => series.visible && series.options.custom?.insightSelectable !== false)
      .flatMap((series) => series.data)
      .filter((point) => {
        const documentCount = point.documentCount ?? point.y;
        return !!documentCount && documentCount >= Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT;
      });

    return eligiblePoints.length;
  }

  override hasEnoughAiInsightEligiblePoints(
    handler: MatchmakerHandler,
    minRequired: number
  ): boolean {
    const chart = this.chartDisplayService.getChart(this.elementContent.nativeElement);
    if (!chart) {
      return false;
    }

    const rule = this.aiInsightValidatorService.getPointSelectionRule(handler);
    const threshold = Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT;

    const eligibleBySeries = new Map<number, number>();
    const eligibleByGroup = new Map<string, number>();
    let totalEligible = 0;

    chart.series
      .filter((series) => series.visible && series.options.custom?.insightSelectable !== false)
      .forEach((series) => {
        const seriesIndex = series.index;

        series.data.forEach((point) => {
          const documentCount = point.documentCount ?? point.y;
          if (!documentCount || documentCount < threshold) {
            return;
          }

          totalEligible += 1;

          const currentSeriesCount = eligibleBySeries.get(seriesIndex) ?? 0;
          eligibleBySeries.set(seriesIndex, currentSeriesCount + 1);

          const groupKey = point.pointId ?? point.category;
          if (groupKey != null) {
            const key = String(groupKey);
            const currentGroupCount = eligibleByGroup.get(key) ?? 0;
            eligibleByGroup.set(key, currentGroupCount + 1);
          }
        });
      });

    switch (rule.rule) {
      case 'SAME_SERIES':
      case 'ALL_SAME_SERIES':
        return Array.from(eligibleBySeries.values()).some((count) => count >= minRequired);

      case 'SAME_GROUP':
      case 'ALL_SAME_GROUP':
        return Array.from(eligibleByGroup.values()).some((count) => count >= minRequired);

      case 'UNIQUE_PER_SERIES':
      case 'MAX_PER_SERIES': {
        const seriesWithAnyEligible = Array.from(eligibleBySeries.values()).filter(
          (count) => count > 0
        ).length;
        return seriesWithAnyEligible >= minRequired;
      }

      default:
        return totalEligible >= minRequired;
    }
  }

  override startAiInsightPointSelectionFlow(handler: MatchmakerHandler) {
    this.chartPointSelectionService.allowPointSelection(
      this.elementContent.nativeElement,
      (selection: { point: any | any[]; isSelected: boolean }[]) =>
        this.aiInsightPointSelection.next(selection),
      this.buildPointSelectionOptions(handler)
    );
  }

  override autoSelectAiInsightPoints(handler: MatchmakerHandler): void {
    const points = this.chartPointSelectionService
      .autoSelectPoints(this.elementContent.nativeElement, this.buildPointSelectionOptions(handler))
      .map((point) => ({ point, isSelected: true }));
    this.aiInsightPointSelection.next(points);
  }

  override endAiInsightPointSelectionFlow() {
    this.chartPointSelectionService.disablePointSelection(this.elementContent.nativeElement);
  }

  private buildPointSelectionOptions(
    handler: MatchmakerHandler
  ): ChartPointSelectionMatchmakerOptions {
    const clickablePoints = this.aiInsightValidatorService.getClickablePoints(handler);

    return {
      minPoints: clickablePoints.min,
      maxPoints: clickablePoints.max,
      pointSelectionRule: this.aiInsightValidatorService.getPointSelectionRule(handler),
    };
  }

  get isSmartSummaryEnabledForPoint(): boolean {
    const docCount =
      this.widgetContextMenu?.documentCount || this.widgetContextMenu?.totalDocumentCountByDate;
    return (
      this.widgetContextMenu &&
      docCount &&
      docCount >= Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT
    );
  }
}
